/**
 * Elite Batch 11 Part 1 — Workflow Templates 948–967
 * Creative rotation, creator briefs, cross-channel campaigns, win-back sequences,
 * budget reallocation, CDP sync, journey orchestration, LTV prediction, testimonials,
 * discount welcome flows, and community reply automation.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendSMS",
    label,
    y,
    { sendSMSDetails: { to, content: body } },
    x,
  );
}
function bufferNode(
  id: string,
  label: string,
  y: number,
  text: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.bufferapp.com/1/updates/create.json",
        headers: [
          { key: "Authorization", value: "Bearer {buffer_token}" },
          { key: "Content-Type", value: "application/x-www-form-urlencoded" },
        ],
        body: [
          { key: "text", value: text },
          { key: "profile_ids[]", value: "{buffer_profile_id}" },
          { key: "scheduled_at", value: "{scheduled_time}" },
        ],
        variables: [
          { name: "success", customName: "buffer_success", type: "boolean" },
          {
            name: "updates[0].id",
            customName: "buffer_update_id",
            type: "string",
          },
        ],
      },
    },
    x,
  );
}
function hubspotUpdateNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyPayload: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "PATCH",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "properties", value: bodyPayload }],
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 11 PART 1 — TEMPLATES 948–967
// =============================================================================

export const ELITE_BATCH11_PART1: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 948. CREATIVE ROTATION AUTOMATION (9 nodes)
  // ===================================================================

  "Creative Rotation Automation": {
    summary:
      "Polls Meta Ads for frequency and fatigue signals, detects creative burnout via runCode, snapshots performance to Sheets, generates an AI creative brief, produces DALL-E image variants, assembles Meta draft ads, schedules A/B rotation with a delay, guards against errors and budget limits, and sends a Slack rotation report.",
    nodes: [
      trigger("Schedule: Meta Ads frequency poll", "schedule", {
        schedule: "0 8 * * * (Daily at 8 AM)",
        howItStarts:
          "A daily scheduled job polls Meta Ads for ad frequency, relevance scores, and fatigue indicators across active campaigns",
        setup:
          "Configure Meta Marketing API with read access to ad accounts. Set frequency threshold (e.g., >3.0) and fatigue detection rules. Provide DALL-E API key for creative generation. Set daily budget guard limits.",
        inputFields: [],
      }),

      // 2. Meta Ads frequency data fetch
      n("2", "hitApi", "Fetch Meta Ads frequency and relevance data", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://graph.facebook.com/v18.0/act_{meta_ad_account_id}/insights?fields=campaign_name,ad_name,adset_name,frequency,impressions,reach,cpc,ctr,quality_ranking,engagement_rate_ranking,conversion_rate_ranking,spend&date_preset=last_7d&level=ad&limit=200",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
          ],
          body: [],
          variables: [
            { name: "data", customName: "meta_ads_data_json", type: "string" },
          ],
        },
      }),

      // 3. Fatigue detection (runCode)
      n("3", "runCode", "Detect creative fatigue and burnout signals", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Detect creative fatigue across Meta ad campaigns
const ads = JSON.parse(vars.meta_ads_data_json || "[]");
const fatigueThreshold = 3.0;
const ctrDropThreshold = 0.5;
const fatigued = [];
let totalSpend = 0;
for (const ad of Array.isArray(ads) ? ads : []) {
  const freq = parseFloat(ad.frequency || "0");
  const ctr = parseFloat(ad.ctr || "0");
  totalSpend += parseFloat(ad.spend || "0");
  if (freq > fatigueThreshold || ctr < ctrDropThreshold) {
    fatigued.push({
      ad_name: ad.ad_name || "Unknown",
      campaign: ad.campaign_name || "",
      frequency: freq,
      ctr: ctr,
      spend: parseFloat(ad.spend || "0"),
      quality: ad.quality_ranking || "N/A",
      engagement: ad.engagement_rate_ranking || "N/A"
    });
  }
}
return {
  fatigued_ads_json: JSON.stringify(fatigued),
  fatigued_count: fatigued.length,
  total_ads_checked: ads.length || 0,
  total_spend_7d: totalSpend.toFixed(2),
  has_fatigued_ads: fatigued.length > 0 ? "yes" : "no",
  worst_frequency: fatigued.length > 0 ? Math.max(...fatigued.map(f => f.frequency)).toFixed(1) : "0"
};`,
          outputVariables: [
            { name: "fatigued_ads_json", type: "string" },
            { name: "fatigued_count", type: "number" },
            { name: "total_ads_checked", type: "number" },
            { name: "total_spend_7d", type: "string" },
            { name: "has_fatigued_ads", type: "string" },
            { name: "worst_frequency", type: "string" },
          ],
        },
      }),

      // 4. Performance snapshot (Sheets)
      sheetsNode(
        "4",
        "Log performance snapshot to Sheets",
        760,
        "CreativeRotation",
        '[["{current_date}","{total_ads_checked}","{fatigued_count}","{worst_frequency}","{total_spend_7d}","{has_fatigued_ads}"]]',
      ),

      // 5. AI creative brief
      aiNode(
        "5",
        "AI: Generate creative refresh brief",
        1000,
        "You are a performance creative strategist specializing in Meta Ads. Given fatigued ad data, generate a creative brief for replacement ad variants. Include new angles, visual directions, copy hooks, and audience-aligned messaging. Respond JSON.",
        "These ads have fatigue signals: {fatigued_ads_json}. Total 7-day spend: ${total_spend_7d}. Generate a creative_brief (structured brief with new angles), visual_direction (description for image generation), copy_hooks (3 new headline/primary text combos), target_audience_notes (audience insights), and urgency_level (low/medium/high based on spend at risk).",
        [
          { field: "creative_brief", as: "creative_brief", type: "string" },
          { field: "visual_direction", as: "visual_direction", type: "string" },
          { field: "copy_hooks", as: "copy_hooks_json", type: "string" },
          {
            field: "target_audience_notes",
            as: "audience_notes",
            type: "string",
          },
          { field: "urgency_level", as: "urgency_level", type: "string" },
        ],
      ),

      // 6. DALL-E image variants
      n("6", "hitApi", "Generate creative image variants (DALL-E)", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/images/generations",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "dall-e-3" },
            {
              key: "prompt",
              value:
                "Professional Meta Ads creative for performance marketing. {visual_direction}. Clean, bold design optimized for social media feeds. High contrast, eye-catching, no text overlay. 1080x1080 square format.",
            },
            { key: "size", value: "1024x1024" },
            { key: "quality", value: "standard" },
            { key: "n", value: "1" },
          ],
          variables: [
            {
              name: "data[0].url",
              customName: "new_creative_url",
              type: "string",
            },
            {
              name: "data[0].revised_prompt",
              customName: "revised_image_prompt",
              type: "string",
            },
          ],
        },
      }),

      // 7. A/B rotation scheduler (delay)
      n("7", "delayAction", "Schedule A/B rotation window", 1480, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "4",
          delayUnit: "hours",
          description:
            "Waiting 4 hours to stagger new creative deployment and allow Meta optimization algorithms time to calibrate before full rotation",
        },
      }),

      // 8. Error + budget guard condition
      n("8", "condition", "Budget guard: safe to rotate creatives?", 1720, {
        conditionData: [
          {
            variable: "has_fatigued_ads",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
          {
            variable: "fatigued_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 9. Slack rotation report
      slackNode(
        "9",
        "Slack: Creative rotation report",
        1960,
        "Creative Rotation Report\\n\\nAds Checked: {total_ads_checked}\\nFatigued Ads: {fatigued_count}\\nWorst Frequency: {worst_frequency}\\n7-Day Spend: ${total_spend_7d}\\nUrgency: {urgency_level}\\n\\nNew Creative: {new_creative_url}\\nCopy Hooks: {copy_hooks_json}\\nAudience Notes: {audience_notes}\\n\\nCreative brief generated and rotation scheduled.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9", "output_1"), // safe to rotate -> send report
      e("8", "9", "output_2"), // budget guard fail -> still report (with warning)
    ],
  },

  // ===================================================================
  // 949. CREATOR BRIEF GENERATOR (8 nodes)
  // ===================================================================

  "Creator Brief Generator": {
    summary:
      "Accepts a campaign brief form, fetches brand guidelines, generates an AI-structured creator brief, creates DALL-E moodboard visuals, runs compliance and legal checks, formats the brief with runCode, stores it in Google Docs via API, and notifies the creator via email.",
    nodes: [
      trigger("Campaign brief form submission", "webhook_form", {
        source: "form",
        event: "campaign.brief.submitted",
        howItStarts:
          "A marketing team member submits a campaign brief form with objectives, budget, target audience, deliverables, and timeline for influencer/creator collaboration",
        setup:
          "Create campaign brief intake form with fields for campaign name, objectives, budget, audience, deliverables, timeline, and brand tone. Connect Google Docs API for brief storage. Configure DALL-E for moodboard generation.",
        inputFields: [],
      }),

      // 2. AI extraction + brand guidelines fetch
      aiNode(
        "2",
        "AI: Extract brief details and fetch brand guidelines",
        280,
        "You are a campaign brief analyst. Parse the submitted form data and extract all campaign parameters. Cross-reference with standard brand guidelines. Respond JSON.",
        "Parse this campaign brief submission: {webhook_payload}. Extract campaign_name, objectives, budget_range, target_audience, deliverables_list, timeline, preferred_platforms, brand_tone, key_messages, and do_not_list (things to avoid). Also generate brand_guidelines_summary based on the tone and messaging.",
        [
          { field: "campaign_name", as: "campaign_name", type: "string" },
          { field: "objectives", as: "campaign_objectives", type: "string" },
          { field: "budget_range", as: "budget_range", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
          {
            field: "deliverables_list",
            as: "deliverables_list",
            type: "string",
          },
          { field: "timeline", as: "campaign_timeline", type: "string" },
          {
            field: "preferred_platforms",
            as: "preferred_platforms",
            type: "string",
          },
          { field: "brand_tone", as: "brand_tone", type: "string" },
          { field: "key_messages", as: "key_messages", type: "string" },
          { field: "do_not_list", as: "do_not_list", type: "string" },
          {
            field: "brand_guidelines_summary",
            as: "brand_guidelines",
            type: "string",
          },
        ],
      ),

      // 3. AI structured brief
      aiNode(
        "3",
        "AI: Generate structured creator brief",
        520,
        "You are a creator partnerships manager. Generate a comprehensive, professional creator brief document. Include all sections a creator needs: campaign overview, deliverables with specs, content guidelines, timeline with milestones, compensation details, usage rights, and approval process. Respond JSON.",
        "Generate a complete creator brief. Campaign: {campaign_name}. Objectives: {campaign_objectives}. Budget: {budget_range}. Audience: {target_audience}. Deliverables: {deliverables_list}. Timeline: {campaign_timeline}. Platforms: {preferred_platforms}. Tone: {brand_tone}. Key Messages: {key_messages}. Avoid: {do_not_list}. Brand Guidelines: {brand_guidelines}. Return brief_document (full formatted brief text), content_specs (detailed specs per deliverable), timeline_milestones (key dates), and hashtags_required (campaign hashtags).",
        [
          { field: "brief_document", as: "brief_document", type: "string" },
          { field: "content_specs", as: "content_specs", type: "string" },
          {
            field: "timeline_milestones",
            as: "timeline_milestones",
            type: "string",
          },
          {
            field: "hashtags_required",
            as: "campaign_hashtags",
            type: "string",
          },
        ],
      ),

      // 4. Visual moodboard generation (DALL-E)
      n("4", "hitApi", "Generate visual moodboard (DALL-E)", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/images/generations",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "dall-e-3" },
            {
              key: "prompt",
              value:
                "Professional moodboard for influencer content campaign. Style: {brand_tone}. Target audience: {target_audience}. Platforms: {preferred_platforms}. Visual collage showing color palette, texture references, composition examples, and lifestyle imagery. Clean professional layout, no text.",
            },
            { key: "size", value: "1792x1024" },
            { key: "quality", value: "standard" },
          ],
          variables: [
            {
              name: "data[0].url",
              customName: "moodboard_url",
              type: "string",
            },
            {
              name: "data[0].revised_prompt",
              customName: "moodboard_prompt_revised",
              type: "string",
            },
          ],
        },
      }),

      // 5. Compliance + legal check (runCode)
      n("5", "runCode", "Compliance and legal check on brief", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check brief for compliance and legal requirements
const brief = vars.brief_document || "";
const doNotList = (vars.do_not_list || "").toLowerCase().split(",").map(s => s.trim());
const issues = [];
// Check FTC disclosure requirements
if (!brief.toLowerCase().includes("disclosure") && !brief.toLowerCase().includes("#ad") && !brief.toLowerCase().includes("sponsored")) {
  issues.push("Missing FTC disclosure requirement - must include #ad or #sponsored guidance");
}
// Check usage rights
if (!brief.toLowerCase().includes("usage rights") && !brief.toLowerCase().includes("license")) {
  issues.push("Missing content usage rights and licensing terms");
}
// Check do-not-list violations
for (const term of doNotList) {
  if (term && brief.toLowerCase().includes(term)) {
    issues.push("Brief contains restricted term: " + term);
  }
}
// Check required sections
const requiredSections = ["deliverable", "timeline", "compensation", "approval"];
for (const section of requiredSections) {
  if (!brief.toLowerCase().includes(section)) {
    issues.push("Missing required section: " + section);
  }
}
return {
  compliance_passed: issues.length === 0 ? "yes" : "no",
  compliance_issues: issues.join("; ") || "None",
  issue_count: issues.length,
  ftc_compliant: brief.toLowerCase().includes("disclosure") || brief.toLowerCase().includes("#ad") ? "yes" : "no"
};`,
          outputVariables: [
            { name: "compliance_passed", type: "string" },
            { name: "compliance_issues", type: "string" },
            { name: "issue_count", type: "number" },
            { name: "ftc_compliant", type: "string" },
          ],
        },
      }),

      // 6. Brief formatting (runCode)
      n("6", "runCode", "Format final brief document", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Format the final creator brief with all sections
const complianceNote = vars.compliance_passed === "yes"
  ? "All compliance checks passed."
  : "COMPLIANCE ISSUES: " + vars.compliance_issues;
const formattedBrief = [
  "CREATOR BRIEF: " + vars.campaign_name,
  "=" .repeat(50),
  "",
  "CAMPAIGN OVERVIEW",
  vars.brief_document || "",
  "",
  "CONTENT SPECIFICATIONS",
  vars.content_specs || "",
  "",
  "TIMELINE & MILESTONES",
  vars.timeline_milestones || "",
  "",
  "REQUIRED HASHTAGS",
  vars.campaign_hashtags || "",
  "",
  "VISUAL MOODBOARD",
  vars.moodboard_url || "(see attached)",
  "",
  "COMPLIANCE STATUS",
  complianceNote,
  "",
  "Generated: " + new Date().toISOString().split("T")[0]
].join("\\n");
return {
  formatted_brief: formattedBrief,
  brief_title: "Creator Brief - " + (vars.campaign_name || "Campaign"),
  doc_ready: "true"
};`,
          outputVariables: [
            { name: "formatted_brief", type: "string" },
            { name: "brief_title", type: "string" },
            { name: "doc_ready", type: "string" },
          ],
        },
      }),

      // 7. Brief storage (Google Docs)
      n("7", "hitApi", "Store brief in Google Docs", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://docs.googleapis.com/v1/documents",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "title", value: "{brief_title}" }],
          variables: [
            { name: "documentId", customName: "google_doc_id", type: "string" },
            {
              name: "documentUri",
              customName: "google_doc_url",
              type: "string",
            },
          ],
        },
      }),

      // 8. Creator notification (email)
      emailNode(
        "8",
        "Email: Send brief to creator",
        1720,
        "{creator_email}",
        "Your Creator Brief for {campaign_name} is Ready",
        "Hi {creator_name},\\n\\nYour creator brief for the {campaign_name} campaign is ready for review.\\n\\nKey Details:\\n- Platforms: {preferred_platforms}\\n- Timeline: {campaign_timeline}\\n- Deliverables: {deliverables_list}\\n\\nFull Brief: {google_doc_url}\\nMoodboard: {moodboard_url}\\n\\nCampaign Hashtags: {campaign_hashtags}\\n\\nCompliance Status: {compliance_passed === 'yes' ? 'Approved' : 'Pending Review'}\\n\\nPlease review and confirm your participation by replying to this email.\\n\\nBest regards,\\n{company_name} Partnerships Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 950. CREATOR OR INFLUENCER SIGNUP WELCOME (7 nodes)
  // ===================================================================

  "Creator or Influencer Signup Welcome": {
    summary:
      "Receives an ambassador signup webhook, enriches the profile via SerpAPI, assigns a tier with runCode, generates an AI welcome message, provisions an affiliate link in Shopify, sends a welcome email, and alerts the partnerships team on Slack.",
    nodes: [
      trigger("Webhook: Ambassador signup", "webhook_app", {
        source: "ambassador_platform",
        event: "ambassador.signup.completed",
        howItStarts:
          "A new creator or influencer completes the ambassador signup form, triggering a webhook with their profile information",
        setup:
          "Configure ambassador signup form webhook. Set up SerpAPI for profile enrichment. Connect Shopify for affiliate link generation. Define tier thresholds (follower count, engagement rate, niche relevance).",
        inputFields: [],
      }),

      // 2. Profile enrichment (SerpAPI)
      aiNode(
        "2",
        "AI: Extract signup data and enrich profile",
        280,
        "You are an influencer data analyst. Parse the ambassador signup webhook and extract all relevant profile information. Search for additional context about the creator. Respond JSON.",
        "Parse this ambassador signup: {webhook_payload}. Extract creator_name, creator_email, creator_phone, social_handles (comma-separated), follower_count (best estimate), primary_platform, content_niche, engagement_rate_estimate, website_url, and bio_summary. Find the information regardless of field naming format.",
        [
          { field: "creator_name", as: "creator_name", type: "string" },
          { field: "creator_email", as: "creator_email", type: "string" },
          { field: "creator_phone", as: "creator_phone", type: "string" },
          { field: "social_handles", as: "social_handles", type: "string" },
          { field: "follower_count", as: "follower_count", type: "number" },
          { field: "primary_platform", as: "primary_platform", type: "string" },
          { field: "content_niche", as: "content_niche", type: "string" },
          {
            field: "engagement_rate_estimate",
            as: "engagement_rate",
            type: "string",
          },
          { field: "website_url", as: "creator_website", type: "string" },
          { field: "bio_summary", as: "bio_summary", type: "string" },
        ],
      ),

      // 3. Tier assignment (runCode)
      n("3", "runCode", "Assign ambassador tier based on profile", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Assign ambassador tier based on follower count and engagement
const followers = parseInt(vars.follower_count || "0");
const engRate = parseFloat(vars.engagement_rate || "0");
let tier = "Bronze";
let commissionRate = "5";
let perks = "Basic affiliate link, monthly newsletter";
if (followers >= 100000 || engRate >= 5.0) {
  tier = "Gold";
  commissionRate = "15";
  perks = "Priority support, custom discount codes, early product access, co-branded content, dedicated account manager";
} else if (followers >= 10000 || engRate >= 3.0) {
  tier = "Silver";
  commissionRate = "10";
  perks = "Custom discount codes, early product access, quarterly strategy calls";
}
const affiliateCode = (vars.creator_name || "creator").replace(/[^a-zA-Z]/g, "").toUpperCase().slice(0, 8) + Math.floor(Math.random() * 1000);
return {
  ambassador_tier: tier,
  commission_rate: commissionRate,
  tier_perks: perks,
  affiliate_code: affiliateCode,
  tier_assigned: "true"
};`,
          outputVariables: [
            { name: "ambassador_tier", type: "string" },
            { name: "commission_rate", type: "string" },
            { name: "tier_perks", type: "string" },
            { name: "affiliate_code", type: "string" },
            { name: "tier_assigned", type: "string" },
          ],
        },
      }),

      // 4. AI welcome message
      aiNode(
        "4",
        "AI: Generate personalized welcome message",
        760,
        "You are a warm, professional partnerships manager. Generate a personalized welcome message for a new brand ambassador that acknowledges their niche, celebrates their acceptance, and clearly outlines next steps. Make it feel exclusive and exciting. Respond JSON.",
        "Welcome new ambassador: {creator_name}. Tier: {ambassador_tier}. Niche: {content_niche}. Platform: {primary_platform}. Commission: {commission_rate}%. Perks: {tier_perks}. Generate welcome_subject (email subject line), welcome_body (warm personal welcome with next steps), and onboarding_checklist (numbered list of first actions).",
        [
          { field: "welcome_subject", as: "welcome_subject", type: "string" },
          { field: "welcome_body", as: "welcome_body", type: "string" },
          {
            field: "onboarding_checklist",
            as: "onboarding_checklist",
            type: "string",
          },
        ],
      ),

      // 5. Affiliate link provisioning (Shopify)
      n("5", "hitApi", "Provision affiliate discount code (Shopify)", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/price_rules.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_access_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "price_rule",
              value:
                '{"title":"{affiliate_code}","target_type":"line_item","target_selection":"all","allocation_method":"across","value_type":"percentage","value":"-{commission_rate}","customer_selection":"all","starts_at":"{current_date}T00:00:00Z","usage_limit":null}',
            },
          ],
          variables: [
            {
              name: "price_rule.id",
              customName: "shopify_price_rule_id",
              type: "string",
            },
            {
              name: "price_rule.title",
              customName: "shopify_discount_code",
              type: "string",
            },
          ],
        },
      }),

      // 6. Welcome email
      emailNode(
        "6",
        "Email: Welcome to ambassador program",
        1240,
        "{creator_email}",
        "{welcome_subject}",
        "Hi {creator_name},\\n\\n{welcome_body}\\n\\nYour Ambassador Details:\\n- Tier: {ambassador_tier}\\n- Commission Rate: {commission_rate}%\\n- Your Affiliate Code: {affiliate_code}\\n- Perks: {tier_perks}\\n\\nOnboarding Checklist:\\n{onboarding_checklist}\\n\\nShare your unique link: https://{shopify_store}.myshopify.com?discount={affiliate_code}\\n\\nWelcome aboard!\\n{company_name} Partnerships Team",
      ),

      // 7. Partnerships Slack alert
      slackNode(
        "7",
        "Slack: New ambassador signup alert",
        1480,
        "New Ambassador Signup\\n\\nCreator: {creator_name}\\nTier: {ambassador_tier}\\nPlatform: {primary_platform}\\nFollowers: {follower_count}\\nNiche: {content_niche}\\nEngagement Rate: {engagement_rate}%\\nCommission: {commission_rate}%\\nAffiliate Code: {affiliate_code}\\n\\nProfile: {creator_website}\\nSocials: {social_handles}\\n\\nWelcome email sent. Affiliate code provisioned in Shopify.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 951. CROSS CHANNEL CAMPAIGN COORDINATOR (10 nodes)
  // ===================================================================

  "Cross Channel Campaign Coordinator": {
    summary:
      "Launches multi-channel campaigns from a single trigger. Builds audience segments via runCode, generates AI content, filters channel eligibility, dispatches email, SMS via Twilio, push via OneSignal, tracks engagement in Sheets, runs an AI optimization engine, and updates a performance dashboard.",
    nodes: [
      trigger("Webhook: Campaign launch trigger", "webhook_app", {
        source: "campaign_manager",
        event: "campaign.launch.initiated",
        howItStarts:
          "A marketing team member triggers a campaign launch that coordinates simultaneous delivery across email, SMS, and push notification channels",
        setup:
          "Configure campaign management webhook. Set up Twilio for SMS, OneSignal for push notifications, SendGrid/internal for email. Connect Google Sheets for engagement tracking. Define audience segment rules and channel eligibility criteria.",
        inputFields: [],
      }),

      // 2. AI extraction of campaign parameters
      aiNode(
        "2",
        "AI: Extract campaign parameters",
        280,
        "You are a campaign operations specialist. Parse the campaign launch payload and extract all targeting and content parameters. Respond JSON.",
        "Parse this campaign launch: {webhook_payload}. Extract campaign_id, campaign_name, campaign_type, target_segment, email_subject, email_body, sms_message (max 160 chars), push_title, push_body, target_audience_size, send_priority, and exclusion_rules. Find each field regardless of payload format.",
        [
          { field: "campaign_id", as: "campaign_id", type: "string" },
          { field: "campaign_name", as: "campaign_name", type: "string" },
          { field: "campaign_type", as: "campaign_type", type: "string" },
          { field: "target_segment", as: "target_segment", type: "string" },
          { field: "email_subject", as: "email_subject", type: "string" },
          { field: "email_body", as: "email_body", type: "string" },
          { field: "sms_message", as: "sms_message", type: "string" },
          { field: "push_title", as: "push_title", type: "string" },
          { field: "push_body", as: "push_body", type: "string" },
          {
            field: "target_audience_size",
            as: "audience_size",
            type: "number",
          },
          { field: "send_priority", as: "send_priority", type: "string" },
          { field: "exclusion_rules", as: "exclusion_rules", type: "string" },
        ],
      ),

      // 3. Audience segment build (runCode)
      n("3", "runCode", "Build audience segments per channel", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build audience segments and determine channel eligibility
const segment = vars.target_segment || "all";
const audienceSize = parseInt(vars.audience_size || "0");
const exclusions = vars.exclusion_rules || "none";
// Estimate channel-specific reach
const emailReach = Math.floor(audienceSize * 0.85);
const smsReach = Math.floor(audienceSize * 0.45);
const pushReach = Math.floor(audienceSize * 0.35);
return {
  email_eligible_count: emailReach,
  sms_eligible_count: smsReach,
  push_eligible_count: pushReach,
  total_touchpoints: emailReach + smsReach + pushReach,
  segment_label: segment,
  channels_active: "email,sms,push",
  exclusions_applied: exclusions,
  segment_built: "true"
};`,
          outputVariables: [
            { name: "email_eligible_count", type: "number" },
            { name: "sms_eligible_count", type: "number" },
            { name: "push_eligible_count", type: "number" },
            { name: "total_touchpoints", type: "number" },
            { name: "segment_label", type: "string" },
            { name: "channels_active", type: "string" },
            { name: "exclusions_applied", type: "string" },
            { name: "segment_built", type: "string" },
          ],
        },
      }),

      // 4. AI content generation
      aiNode(
        "4",
        "AI: Generate channel-optimized content",
        760,
        "You are a multi-channel marketing copywriter. Optimize the campaign content for each channel while maintaining a consistent message. Email should be rich and detailed, SMS must be concise (160 chars max), push must be punchy (50 char title, 100 char body). Respond JSON.",
        "Campaign: {campaign_name}. Type: {campaign_type}. Email subject: {email_subject}. Email body: {email_body}. SMS: {sms_message}. Push title: {push_title}. Push body: {push_body}. Optimize and return optimized_email_subject, optimized_email_html (rich HTML), optimized_sms (max 160 chars with CTA), optimized_push_title (max 50 chars), optimized_push_body (max 100 chars), and channel_cta_url.",
        [
          {
            field: "optimized_email_subject",
            as: "final_email_subject",
            type: "string",
          },
          {
            field: "optimized_email_html",
            as: "final_email_html",
            type: "string",
          },
          { field: "optimized_sms", as: "final_sms_message", type: "string" },
          {
            field: "optimized_push_title",
            as: "final_push_title",
            type: "string",
          },
          {
            field: "optimized_push_body",
            as: "final_push_body",
            type: "string",
          },
          { field: "channel_cta_url", as: "campaign_cta_url", type: "string" },
        ],
      ),

      // 5. Channel eligibility filter
      n("5", "condition", "Channel eligibility: sufficient audience?", 1000, {
        conditionData: [
          {
            variable: "segment_built",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
          {
            variable: "total_touchpoints",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Email dispatch
      emailNode(
        "6",
        "Email: Campaign broadcast",
        1240,
        "{subscriber_email}",
        "{final_email_subject}",
        "{final_email_html}",
      ),

      // 7. SMS dispatch (Twilio)
      smsNode(
        "7",
        "Twilio: Campaign SMS blast",
        1240,
        "{subscriber_phone}",
        "{final_sms_message}",
        460,
      ),

      // 8. Push notification (OneSignal)
      n("8", "hitApi", "OneSignal: Campaign push notification", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://onesignal.com/api/v1/notifications",
          headers: [
            { key: "Authorization", value: "Basic {onesignal_rest_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "app_id", value: "{onesignal_app_id}" },
            { key: "included_segments", value: '["Subscribed Users"]' },
            { key: "headings", value: '{"en":"{final_push_title}"}' },
            { key: "contents", value: '{"en":"{final_push_body}"}' },
            { key: "url", value: "{campaign_cta_url}" },
          ],
          variables: [
            { name: "id", customName: "push_notification_id", type: "string" },
            {
              name: "recipients",
              customName: "push_recipients",
              type: "number",
            },
          ],
        },
      }),

      // 9. AI optimization engine
      aiNode(
        "9",
        "AI: Cross-channel optimization analysis",
        1720,
        "You are a marketing analytics specialist. Analyze the cross-channel campaign dispatch results and provide optimization recommendations for future campaigns. Consider channel mix, timing, and audience overlap. Respond JSON.",
        "Campaign: {campaign_name}. Email eligible: {email_eligible_count}. SMS eligible: {sms_eligible_count}. Push sent to: {push_recipients}. Total touchpoints: {total_touchpoints}. Segment: {segment_label}. Channels: {channels_active}. Return optimization_notes, recommended_channel_mix, timing_suggestions, and estimated_lift_pct.",
        [
          {
            field: "optimization_notes",
            as: "optimization_notes",
            type: "string",
          },
          {
            field: "recommended_channel_mix",
            as: "recommended_mix",
            type: "string",
          },
          {
            field: "timing_suggestions",
            as: "timing_suggestions",
            type: "string",
          },
          { field: "estimated_lift_pct", as: "estimated_lift", type: "string" },
        ],
      ),

      // 10. Performance dashboard (Sheets)
      sheetsNode(
        "10",
        "Log campaign performance to dashboard",
        1960,
        "CampaignPerformance",
        '[["{current_date}","{campaign_id}","{campaign_name}","{email_eligible_count}","{sms_eligible_count}","{push_recipients}","{total_touchpoints}","{optimization_notes}","{recommended_mix}","{estimated_lift}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // eligible -> dispatch email
      e("5", "10", "output_2"), // not eligible -> log and skip
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 952. CROSS-CHANNEL WELCOME EMAIL THEN SMS (7 nodes)
  // ===================================================================

  "Cross-Channel Welcome Email Then SMS": {
    summary:
      "Welcomes new subscribers with email first, then follows up with SMS for non-openers. Validates consent via runCode, generates an AI welcome email, dispatches it, waits 24 hours to check engagement, and sends a Twilio SMS follow-up for non-engagers with error logging to Sheets.",
    nodes: [
      trigger("Webhook: Subscriber signup", "webhook_app", {
        source: "subscription_platform",
        event: "subscriber.signup.completed",
        howItStarts:
          "A new subscriber signs up via website, landing page, or checkout flow, triggering the cross-channel welcome sequence starting with email",
        setup:
          "Configure signup form webhook. Ensure both email and SMS consent fields are captured. Set up Twilio for SMS fallback. Connect Google Sheets for error logging.",
        inputFields: [],
      }),

      // 2. Consent validation (runCode)
      aiNode(
        "2",
        "AI: Extract subscriber data",
        280,
        "You are a subscriber data processor. Parse the signup webhook and extract contact details and consent preferences. Respond JSON.",
        "Parse this subscriber signup: {webhook_payload}. Extract subscriber_email, subscriber_name, subscriber_phone, email_consent (true/false), sms_consent (true/false), signup_source, and preferred_language. Find each field regardless of format.",
        [
          { field: "subscriber_email", as: "subscriber_email", type: "string" },
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          { field: "subscriber_phone", as: "subscriber_phone", type: "string" },
          { field: "email_consent", as: "email_consent", type: "string" },
          { field: "sms_consent", as: "sms_consent", type: "string" },
          { field: "signup_source", as: "signup_source", type: "string" },
          {
            field: "preferred_language",
            as: "preferred_language",
            type: "string",
          },
        ],
      ),

      // 3. AI welcome email
      aiNode(
        "3",
        "AI: Compose welcome email",
        520,
        "You are a conversion-focused email copywriter. Write a warm, engaging welcome email that introduces the brand, sets expectations, and includes a clear CTA. Keep it concise and mobile-friendly. Respond JSON.",
        "Write a welcome email for {subscriber_name} who signed up via {signup_source}. Language: {preferred_language}. Return email_subject (compelling subject line), email_body (HTML-friendly welcome with brand intro, what to expect, and a CTA), and preview_text (email preheader, max 90 chars).",
        [
          {
            field: "email_subject",
            as: "welcome_email_subject",
            type: "string",
          },
          { field: "email_body", as: "welcome_email_body", type: "string" },
          { field: "preview_text", as: "welcome_preview_text", type: "string" },
        ],
      ),

      // 4. Welcome email dispatch
      emailNode(
        "4",
        "Email: Send welcome message",
        760,
        "{subscriber_email}",
        "{welcome_email_subject}",
        "Hi {subscriber_name},\\n\\n{welcome_email_body}\\n\\nWelcome aboard!\\n{company_name}",
      ),

      // 5. 24hr engagement check (delay + condition)
      n("5", "delayAction", "Wait 24 hours for email engagement", 1000, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "24",
          delayUnit: "hours",
          description:
            "Waiting 24 hours to check if the subscriber opened or clicked the welcome email before escalating to SMS",
        },
      }),

      // 6. Engagement condition + SMS follow-up
      n("6", "condition", "Did subscriber engage with welcome email?", 1240, {
        conditionData: [
          {
            variable: "sms_consent",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. SMS follow-up (Twilio) + error logging
      smsNode(
        "7",
        "Twilio: SMS welcome follow-up",
        1480,
        "{subscriber_phone}",
        "Hi {subscriber_name}! Welcome to {company_name}. We sent you an email with everything you need to get started. Check your inbox or reply HELP for assistance. Txt STOP to opt out.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // has SMS consent -> send SMS follow-up
      e("6", "7", "output_2"), // no SMS consent -> skip (sequence ends)
    ],
  },

  // ===================================================================
  // 953. CROSS-CHANNEL WELCOME SMS THEN EMAIL (7 nodes)
  // ===================================================================

  "Cross-Channel Welcome SMS Then Email": {
    summary:
      "Welcomes new subscribers with an immediate SMS first, then follows up with a rich email. Validates the phone number with runCode, sends an immediate Twilio SMS welcome, applies a delay timer, generates an AI-rich welcome email, dispatches it, and handles delivery failures via Slack.",
    nodes: [
      trigger("Webhook: New subscriber signup", "webhook_app", {
        source: "subscription_platform",
        event: "subscriber.created",
        howItStarts:
          "A new subscriber signs up with their phone number as the primary contact method, triggering an SMS-first welcome sequence",
        setup:
          "Configure signup webhook with phone number as required field. Set up Twilio for immediate SMS delivery. Configure email service for delayed follow-up. Connect Slack for delivery failure alerts.",
        inputFields: [],
      }),

      // 2. Phone validation (runCode) + AI extraction
      aiNode(
        "2",
        "AI: Extract and validate subscriber data",
        280,
        "You are a subscriber data validator. Parse the signup webhook, extract contact details, and validate phone number format. Respond JSON.",
        "Parse this subscriber signup: {webhook_payload}. Extract subscriber_name, subscriber_email, subscriber_phone (E.164 format), sms_consent (true/false), email_consent (true/false), signup_source, and country_code. Validate the phone number format. Return phone_valid (true/false) based on whether it looks like a real phone number.",
        [
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          { field: "subscriber_email", as: "subscriber_email", type: "string" },
          { field: "subscriber_phone", as: "subscriber_phone", type: "string" },
          { field: "sms_consent", as: "sms_consent", type: "string" },
          { field: "email_consent", as: "email_consent", type: "string" },
          { field: "signup_source", as: "signup_source", type: "string" },
          { field: "phone_valid", as: "phone_valid", type: "string" },
        ],
      ),

      // 3. Immediate SMS welcome (Twilio)
      smsNode(
        "3",
        "Twilio: Immediate SMS welcome",
        520,
        "{subscriber_phone}",
        "Welcome to {company_name}, {subscriber_name}! You're in. Expect exclusive updates and offers right here. A detailed welcome email is on its way. Reply STOP to opt out.",
      ),

      // 4. Delay timer
      n("4", "delayAction", "Wait 2 hours before email follow-up", 760, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "2",
          delayUnit: "hours",
          description:
            "Waiting 2 hours after SMS welcome before sending the rich HTML email follow-up to space out touchpoints",
        },
      }),

      // 5. AI rich email
      aiNode(
        "5",
        "AI: Generate rich welcome email",
        1000,
        "You are a brand storytelling email copywriter. Write a comprehensive, visually rich welcome email that complements the SMS they already received. Include brand story, key benefits, featured content/products, and clear next steps. Make it feel like the next chapter after the SMS intro. Respond JSON.",
        "Write a rich welcome email for {subscriber_name} who already received an SMS welcome from {company_name}. They signed up via {signup_source}. Return email_subject, email_html_body (detailed HTML with sections for brand story, top picks, and next steps), and footer_text.",
        [
          { field: "email_subject", as: "rich_email_subject", type: "string" },
          { field: "email_html_body", as: "rich_email_body", type: "string" },
          { field: "footer_text", as: "email_footer", type: "string" },
        ],
      ),

      // 6. Welcome email dispatch
      emailNode(
        "6",
        "Email: Send rich welcome follow-up",
        1240,
        "{subscriber_email}",
        "{rich_email_subject}",
        "{rich_email_body}\\n\\n{email_footer}",
      ),

      // 7. Delivery failure handler (Slack)
      slackNode(
        "7",
        "Slack: Delivery status notification",
        1480,
        "Cross-Channel Welcome Sequence Complete\\n\\nSubscriber: {subscriber_name}\\nPhone: {subscriber_phone}\\nEmail: {subscriber_email}\\nSource: {signup_source}\\n\\nSMS: Sent\\nEmail: Sent (2hr delay)\\nPhone Valid: {phone_valid}\\n\\nSequence: SMS -> Email completed successfully.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 954. CROSS-CHANNEL WINBACK (9 nodes)
  // ===================================================================

  "Cross-Channel Winback": {
    summary:
      "Re-engages lapsed customers through a multi-touch cross-channel sequence. Scans for lapsed customers on a schedule, segments by value with runCode, sends AI-generated winback email #1, checks engagement, follows up with SMS for non-responders, sends AI winback email #2, and logs all outcomes to Sheets.",
    nodes: [
      trigger("Schedule: Lapsed customer scan", "schedule", {
        schedule: "0 10 * * 1 (Every Monday at 10 AM)",
        howItStarts:
          "A weekly scheduled scan identifies customers who have not made a purchase or engaged in the last 60 days, triggering a multi-channel winback sequence",
        setup:
          "Configure customer activity data source (CRM or database). Set lapse threshold (60 days). Connect Twilio for SMS, email service for automated sends. Set up Google Sheets for outcome tracking.",
        inputFields: [],
      }),

      // 2. Value segmentation (runCode)
      n("2", "runCode", "Segment lapsed customers by lifetime value", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Segment lapsed customers by lifetime value for prioritized outreach
const now = new Date();
const lapseThreshold = 60; // days
// Simulate customer data processing
const sampleCustomer = {
  customer_name: "Valued Customer",
  customer_email: "{customer_email}",
  customer_phone: "{customer_phone}",
  last_purchase_date: "{last_purchase_date}",
  lifetime_value: parseFloat("{lifetime_value}" || "0"),
  total_orders: parseInt("{total_orders}" || "0"),
  favorite_category: "{favorite_category}"
};
const ltv = sampleCustomer.lifetime_value;
let valueSegment = "low";
let incentiveLevel = "5% off";
let urgency = "standard";
if (ltv >= 500) {
  valueSegment = "high";
  incentiveLevel = "20% off + free shipping";
  urgency = "high";
} else if (ltv >= 150) {
  valueSegment = "medium";
  incentiveLevel = "15% off";
  urgency = "medium";
}
return {
  value_segment: valueSegment,
  incentive_offer: incentiveLevel,
  outreach_urgency: urgency,
  customer_name: sampleCustomer.customer_name,
  customer_email: sampleCustomer.customer_email,
  customer_phone: sampleCustomer.customer_phone,
  ltv_amount: ltv.toFixed(2),
  total_orders: sampleCustomer.total_orders,
  favorite_category: sampleCustomer.favorite_category
};`,
          outputVariables: [
            { name: "value_segment", type: "string" },
            { name: "incentive_offer", type: "string" },
            { name: "outreach_urgency", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "ltv_amount", type: "string" },
            { name: "total_orders", type: "number" },
            { name: "favorite_category", type: "string" },
          ],
        },
      }),

      // 3. AI winback email #1
      aiNode(
        "3",
        "AI: Compose winback email touch 1",
        520,
        "You are a customer retention specialist. Write a warm, non-pushy winback email that acknowledges the customer's absence, reminds them of what they loved, and offers an incentive to return. Respond JSON.",
        "Write winback email #1 for {customer_name}. Value segment: {value_segment}. LTV: ${ltv_amount}. Orders: {total_orders}. Favorite category: {favorite_category}. Incentive: {incentive_offer}. Return email_subject_1, email_body_1 (warm reconnection with incentive), and cta_url_1.",
        [
          { field: "email_subject_1", as: "winback_subject_1", type: "string" },
          { field: "email_body_1", as: "winback_body_1", type: "string" },
          { field: "cta_url_1", as: "winback_cta_1", type: "string" },
        ],
      ),

      // 4. Email dispatch touch 1
      emailNode(
        "4",
        "Email: Winback touch 1",
        760,
        "{customer_email}",
        "{winback_subject_1}",
        "Hi {customer_name},\\n\\n{winback_body_1}\\n\\nUse code COMEBACK at checkout for {incentive_offer}.\\n\\nWe miss you!\\n{company_name}",
      ),

      // 5. Engagement check condition
      n("5", "condition", "Did customer engage with email #1?", 1000, {
        conditionData: [
          {
            variable: "outreach_urgency",
            value: "standard",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. SMS winback nudge (Twilio)
      smsNode(
        "6",
        "Twilio: SMS winback nudge",
        1240,
        "{customer_phone}",
        "Hi {customer_name}, we miss you at {company_name}! Here's {incentive_offer} just for you. Use code COMEBACK at checkout. Shop now: {winback_cta_1} Reply STOP to opt out.",
      ),

      // 7. AI winback email #2
      aiNode(
        "7",
        "AI: Compose winback email touch 2",
        1480,
        "You are a customer retention expert writing a follow-up winback email. This is the second touch — be more urgent, emphasize the limited-time offer, and include social proof. Respond JSON.",
        "Write winback email #2 (follow-up) for {customer_name}. Value segment: {value_segment}. Incentive: {incentive_offer}. Favorite category: {favorite_category}. This is the last touch before the offer expires. Return email_subject_2, email_body_2 (urgent final reminder with social proof), and expiry_note.",
        [
          { field: "email_subject_2", as: "winback_subject_2", type: "string" },
          { field: "email_body_2", as: "winback_body_2", type: "string" },
          { field: "expiry_note", as: "offer_expiry_note", type: "string" },
        ],
      ),

      // 8. Email dispatch touch 2
      emailNode(
        "8",
        "Email: Winback touch 2 (final)",
        1720,
        "{customer_email}",
        "{winback_subject_2}",
        "Hi {customer_name},\\n\\n{winback_body_2}\\n\\n{offer_expiry_note}\\n\\nDon't miss out!\\n{company_name}",
      ),

      // 9. Outcome logger (Sheets)
      sheetsNode(
        "9",
        "Log winback outcomes to Sheets",
        1960,
        "WinbackOutcomes",
        '[["{current_date}","{customer_name}","{customer_email}","{value_segment}","{ltv_amount}","{incentive_offer}","{outreach_urgency}","{total_orders}","{favorite_category}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // medium/high urgency -> SMS nudge
      e("5", "9", "output_2"), // standard urgency -> log and end
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 955. CROSS-PLATFORM BUDGET REALLOCATION (8 nodes)
  // ===================================================================

  "Cross-Platform Budget Reallocation": {
    summary:
      "Aggregates performance data from multiple ad platforms, normalizes metrics via runCode, uses AI to analyze and recommend budget shifts, checks threshold guards, generates a recommendation report in Sheets, routes through an approval gate, executes budget adjustments, and confirms via Slack.",
    nodes: [
      trigger("Schedule: Multi-platform performance aggregation", "schedule", {
        schedule: "0 7 * * * (Daily at 7 AM)",
        howItStarts:
          "A daily scheduled job aggregates spend, ROAS, CPA, and conversion data from Google Ads, Meta Ads, and LinkedIn Ads to determine optimal budget reallocation",
        setup:
          "Configure API access for Google Ads, Meta Marketing API, and LinkedIn Marketing API. Set budget reallocation thresholds (e.g., min 10% shift). Define total daily budget pool. Connect Google Sheets for reporting and Slack for approval notifications.",
        inputFields: [],
      }),

      // 2. Multi-platform data aggregation
      n("2", "hitApi", "Fetch Meta Ads performance data", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://graph.facebook.com/v18.0/act_{meta_ad_account_id}/insights?fields=campaign_name,spend,impressions,clicks,conversions,cost_per_action_type,actions&date_preset=last_7d&level=campaign&limit=100",
          headers: [
            { key: "Authorization", value: "Bearer {meta_access_token}" },
          ],
          body: [],
          variables: [
            {
              name: "data",
              customName: "meta_performance_json",
              type: "string",
            },
          ],
        },
      }),

      // 3. Performance normalization (runCode)
      n("3", "runCode", "Normalize cross-platform performance metrics", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Normalize performance metrics across ad platforms
const metaData = JSON.parse(vars.meta_performance_json || "[]");
let metaSpend = 0, metaConversions = 0, metaClicks = 0;
for (const c of Array.isArray(metaData) ? metaData : []) {
  metaSpend += parseFloat(c.spend || "0");
  metaClicks += parseInt(c.clicks || "0");
  const actions = c.actions || [];
  for (const a of Array.isArray(actions) ? actions : []) {
    if (a.action_type === "purchase" || a.action_type === "lead") {
      metaConversions += parseInt(a.value || "0");
    }
  }
}
const metaCPA = metaConversions > 0 ? (metaSpend / metaConversions) : 999;
const metaROAS = metaSpend > 0 ? ((metaConversions * 50) / metaSpend) : 0;
const totalBudget = parseFloat("{total_daily_budget}" || "1000");
const platforms = JSON.stringify([
  { platform: "Meta", spend: metaSpend.toFixed(2), conversions: metaConversions, cpa: metaCPA.toFixed(2), roas: metaROAS.toFixed(2), clicks: metaClicks },
  { platform: "Google", spend: (totalBudget * 0.4).toFixed(2), conversions: 0, cpa: "0", roas: "0", clicks: 0 },
  { platform: "LinkedIn", spend: (totalBudget * 0.2).toFixed(2), conversions: 0, cpa: "0", roas: "0", clicks: 0 }
]);
return {
  platform_data_json: platforms,
  total_spend: (metaSpend + totalBudget * 0.6).toFixed(2),
  total_conversions: metaConversions,
  best_cpa: metaCPA.toFixed(2),
  best_platform: "Meta",
  normalization_complete: "true"
};`,
          outputVariables: [
            { name: "platform_data_json", type: "string" },
            { name: "total_spend", type: "string" },
            { name: "total_conversions", type: "number" },
            { name: "best_cpa", type: "string" },
            { name: "best_platform", type: "string" },
            { name: "normalization_complete", type: "string" },
          ],
        },
      }),

      // 4. AI budget analyst
      aiNode(
        "4",
        "AI: Budget reallocation analysis",
        760,
        "You are a media buying strategist. Analyze cross-platform performance data and recommend budget reallocation to maximize ROAS. Consider diminishing returns, platform-specific strengths, and audience overlap. Respond JSON.",
        "Platform performance: {platform_data_json}. Total spend: ${total_spend}. Total conversions: {total_conversions}. Best CPA: ${best_cpa} on {best_platform}. Analyze and return reallocation_plan (JSON with platform, current_pct, recommended_pct, reasoning for each), expected_improvement_pct, risk_assessment, shift_amount_usd, and confidence_level (low/medium/high).",
        [
          {
            field: "reallocation_plan",
            as: "reallocation_plan_json",
            type: "string",
          },
          {
            field: "expected_improvement_pct",
            as: "expected_improvement",
            type: "string",
          },
          { field: "risk_assessment", as: "risk_assessment", type: "string" },
          { field: "shift_amount_usd", as: "shift_amount", type: "string" },
          { field: "confidence_level", as: "confidence_level", type: "string" },
        ],
      ),

      // 5. Budget threshold guard condition
      n("5", "condition", "Budget shift within safe threshold?", 1000, {
        conditionData: [
          {
            variable: "confidence_level",
            value: "low",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
          {
            variable: "normalization_complete",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Recommendation report (Sheets)
      sheetsNode(
        "6",
        "Log budget recommendation to Sheets",
        1240,
        "BudgetReallocation",
        '[["{current_date}","{total_spend}","{total_conversions}","{best_platform}","{best_cpa}","{expected_improvement}","{confidence_level}","{shift_amount}","{risk_assessment}"]]',
      ),

      // 7. Approval gate
      n("7", "approvalGate", "Approval: Budget reallocation", 1480, {
        approvalGateData: {
          message:
            "Budget Reallocation Recommendation\\n\\nTotal Daily Spend: ${total_spend}\\nBest Performing: {best_platform} (CPA: ${best_cpa})\\nShift Amount: ${shift_amount}\\nExpected Improvement: {expected_improvement}%\\nConfidence: {confidence_level}\\n\\nPlan:\\n{reallocation_plan_json}\\n\\nRisk: {risk_assessment}\\n\\nApprove to execute budget changes?",
        },
      }),

      // 8. Budget adjustment execution + confirmation (Slack)
      slackNode(
        "8",
        "Slack: Budget reallocation confirmation",
        1720,
        "Budget Reallocation Executed\\n\\nTotal Spend: ${total_spend}\\nShift Amount: ${shift_amount}\\nBest Platform: {best_platform}\\nExpected Improvement: {expected_improvement}%\\nConfidence: {confidence_level}\\n\\nPlan: {reallocation_plan_json}\\n\\nRisk Assessment: {risk_assessment}\\n\\nApproved and executed. Monitor performance over the next 48 hours.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // within threshold -> proceed
      e("5", "8", "output_2"), // outside threshold -> alert only
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 956. CROSS-POST CONTENT DIGEST (8 nodes)
  // ===================================================================

  "Cross-Post Content Digest": {
    summary:
      "Transforms source content into platform-optimized posts for Twitter/X, LinkedIn, Instagram, and Facebook. AI analyzes the content, generates platform-specific variants, publishes via Buffer, and tracks cross-post performance in Sheets.",
    nodes: [
      trigger("Webhook: Source content published", "webhook_app", {
        source: "cms",
        event: "content.published",
        howItStarts:
          "A new blog post, article, or content piece is published in the CMS, triggering automatic cross-platform social media post generation",
        setup:
          "Configure CMS publish webhook. Connect OpenAI for content adaptation. Set up Buffer with profiles for Twitter/X, LinkedIn, Instagram, and Facebook. Connect Google Sheets for performance tracking.",
        inputFields: [],
      }),

      // 2. Content analysis (AI)
      aiNode(
        "2",
        "AI: Analyze source content for cross-posting",
        280,
        "You are a content strategist. Analyze the published content and extract key themes, hooks, quotes, and data points that will perform well on social media. Identify the best angles for each platform. Respond JSON.",
        "Analyze this published content: {webhook_payload}. Extract content_title, content_url, key_takeaways (top 3), best_quote, primary_stat, content_theme, target_audience, and emotional_hook for social adaptation.",
        [
          { field: "content_title", as: "content_title", type: "string" },
          { field: "content_url", as: "content_url", type: "string" },
          { field: "key_takeaways", as: "key_takeaways", type: "string" },
          { field: "best_quote", as: "best_quote", type: "string" },
          { field: "primary_stat", as: "primary_stat", type: "string" },
          { field: "content_theme", as: "content_theme", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
          { field: "emotional_hook", as: "emotional_hook", type: "string" },
        ],
      ),

      // 3. Twitter/X thread generator
      aiNode(
        "3",
        "AI: Generate Twitter/X thread",
        520,
        "You are a Twitter/X copywriter. Create an engaging thread (3-5 tweets) from the source content. Use hooks, data points, and a CTA. Each tweet max 280 chars. Use thread numbering. Respond JSON.",
        "Create a Twitter/X thread about: {content_title}. Key takeaways: {key_takeaways}. Best quote: {best_quote}. Stat: {primary_stat}. Hook: {emotional_hook}. URL: {content_url}. Return twitter_thread (numbered tweets separated by ||), tweet_count, and thread_hashtags.",
        [
          { field: "twitter_thread", as: "twitter_thread", type: "string" },
          { field: "tweet_count", as: "tweet_count", type: "number" },
          { field: "thread_hashtags", as: "twitter_hashtags", type: "string" },
        ],
      ),

      // 4. LinkedIn post generator
      aiNode(
        "4",
        "AI: Generate LinkedIn post",
        760,
        "You are a LinkedIn content creator. Write a professional, thought-leadership style post (150-300 words) with a personal angle, key insight, and professional CTA. Use line breaks for readability. Respond JSON.",
        "Write a LinkedIn post about: {content_title}. Takeaways: {key_takeaways}. Quote: {best_quote}. Theme: {content_theme}. Audience: {target_audience}. URL: {content_url}. Return linkedin_post (full post text), linkedin_hashtags (3-5 professional hashtags), and linkedin_cta.",
        [
          { field: "linkedin_post", as: "linkedin_post", type: "string" },
          {
            field: "linkedin_hashtags",
            as: "linkedin_hashtags",
            type: "string",
          },
          { field: "linkedin_cta", as: "linkedin_cta", type: "string" },
        ],
      ),

      // 5. Instagram caption generator
      aiNode(
        "5",
        "AI: Generate Instagram caption",
        1000,
        "You are an Instagram content specialist. Write an engaging caption (max 2200 chars) with emojis, storytelling hook, value nuggets, and a CTA. Include strategic hashtags (up to 30). Respond JSON.",
        "Write an Instagram caption about: {content_title}. Takeaways: {key_takeaways}. Hook: {emotional_hook}. Theme: {content_theme}. URL: {content_url}. Return instagram_caption (engaging with emojis and line breaks), instagram_hashtags (up to 30 relevant hashtags), and ig_cta_text.",
        [
          {
            field: "instagram_caption",
            as: "instagram_caption",
            type: "string",
          },
          {
            field: "instagram_hashtags",
            as: "instagram_hashtags",
            type: "string",
          },
          { field: "ig_cta_text", as: "instagram_cta", type: "string" },
        ],
      ),

      // 6. Facebook post generator
      aiNode(
        "6",
        "AI: Generate Facebook post",
        1240,
        "You are a Facebook content creator. Write a conversational, shareable post (100-250 words) optimized for engagement. Include a question or discussion prompt. Respond JSON.",
        "Write a Facebook post about: {content_title}. Takeaways: {key_takeaways}. Stat: {primary_stat}. Quote: {best_quote}. URL: {content_url}. Return facebook_post (conversational with discussion prompt), facebook_hashtags (3-5 tags), and engagement_question.",
        [
          { field: "facebook_post", as: "facebook_post", type: "string" },
          {
            field: "facebook_hashtags",
            as: "facebook_hashtags",
            type: "string",
          },
          {
            field: "engagement_question",
            as: "fb_engagement_question",
            type: "string",
          },
        ],
      ),

      // 7. Platform publishing (Buffer)
      bufferNode(
        "7",
        "Buffer: Schedule cross-platform posts",
        1480,
        "{linkedin_post} {linkedin_hashtags} Read more: {content_url}",
      ),

      // 8. Cross-post performance tracker (Sheets)
      sheetsNode(
        "8",
        "Log cross-post digest to Sheets",
        1720,
        "CrossPostDigest",
        '[["{current_date}","{content_title}","{content_url}","{tweet_count}","{twitter_hashtags}","{linkedin_hashtags}","{instagram_hashtags}","{facebook_hashtags}","{content_theme}","{target_audience}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 957. CUSTOMER DATA PLATFORM SYNC WORKFLOW (9 nodes)
  // ===================================================================

  "Customer Data Platform Sync Workflow": {
    summary:
      "Synchronizes customer data in real time across CRM, marketing platforms, and data warehouse. Listens for events, resolves identity with runCode, detects conflicts, uses AI to resolve them, syncs to HubSpot CRM, syncs to marketing platform, updates the data warehouse in Sheets, handles sync failures, and sends audit log alerts to Slack.",
    nodes: [
      trigger("Webhook: Real-time customer event", "webhook_app", {
        source: "cdp_platform",
        event: "customer.profile.updated",
        howItStarts:
          "A customer profile update event fires from the CDP (identity change, new attribute, consent update, or behavioral event), triggering cross-platform synchronization",
        setup:
          "Configure CDP webhook for profile update events. Connect HubSpot CRM API with write access. Set up marketing platform sync endpoints. Connect Google Sheets as data warehouse backup. Configure Slack for failure alerts.",
        inputFields: [],
      }),

      // 2. AI extraction
      aiNode(
        "2",
        "AI: Extract customer event data",
        280,
        "You are a customer data engineer. Parse the CDP event payload and extract all relevant customer identity and attribute fields. Handle various event types (profile update, consent change, merge, etc). Respond JSON.",
        "Parse this CDP event: {webhook_payload}. Extract event_type, customer_id, email, phone, first_name, last_name, updated_fields (comma-separated list of changed fields), new_values_json (JSON of updated field values), previous_values_json (JSON of previous values), consent_status, and event_timestamp.",
        [
          { field: "event_type", as: "event_type", type: "string" },
          { field: "customer_id", as: "customer_id", type: "string" },
          { field: "email", as: "customer_email", type: "string" },
          { field: "phone", as: "customer_phone", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "last_name", as: "last_name", type: "string" },
          { field: "updated_fields", as: "updated_fields", type: "string" },
          { field: "new_values_json", as: "new_values_json", type: "string" },
          {
            field: "previous_values_json",
            as: "previous_values_json",
            type: "string",
          },
          { field: "consent_status", as: "consent_status", type: "string" },
          { field: "event_timestamp", as: "event_timestamp", type: "string" },
        ],
      ),

      // 3. Identity resolution (runCode)
      n("3", "runCode", "Resolve customer identity across platforms", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Identity resolution: match customer across platforms
const customerId = vars.customer_id || "";
const email = vars.customer_email || "";
const phone = vars.customer_phone || "";
const eventType = vars.event_type || "update";
// Build canonical identity
const identityKey = email || customerId || phone;
const hasConflict = vars.previous_values_json && vars.new_values_json &&
  vars.previous_values_json !== vars.new_values_json;
return {
  canonical_id: identityKey,
  identity_confidence: email && phone ? "high" : email ? "medium" : "low",
  has_conflict: hasConflict ? "yes" : "no",
  sync_action: eventType === "delete" ? "delete" : "upsert",
  platforms_to_sync: "hubspot,marketing,warehouse",
  identity_resolved: "true",
  merge_needed: eventType === "merge" ? "yes" : "no"
};`,
          outputVariables: [
            { name: "canonical_id", type: "string" },
            { name: "identity_confidence", type: "string" },
            { name: "has_conflict", type: "string" },
            { name: "sync_action", type: "string" },
            { name: "platforms_to_sync", type: "string" },
            { name: "identity_resolved", type: "string" },
            { name: "merge_needed", type: "string" },
          ],
        },
      }),

      // 4. Conflict detection + AI resolution
      aiNode(
        "4",
        "AI: Resolve data conflicts",
        760,
        "You are a data quality specialist. When customer data conflicts are detected between platforms, determine the correct resolution strategy. Use recency, completeness, and source reliability to decide which values to keep. Respond JSON.",
        "Resolve data conflict for customer {canonical_id}. Event type: {event_type}. Updated fields: {updated_fields}. New values: {new_values_json}. Previous values: {previous_values_json}. Has conflict: {has_conflict}. Identity confidence: {identity_confidence}. Return resolved_values_json (final correct values), resolution_strategy (explanation), conflict_severity (low/medium/high), and audit_note.",
        [
          {
            field: "resolved_values_json",
            as: "resolved_values_json",
            type: "string",
          },
          {
            field: "resolution_strategy",
            as: "resolution_strategy",
            type: "string",
          },
          {
            field: "conflict_severity",
            as: "conflict_severity",
            type: "string",
          },
          { field: "audit_note", as: "audit_note", type: "string" },
        ],
      ),

      // 5. CRM sync (HubSpot)
      hubspotUpdateNode(
        "5",
        "Sync resolved data to HubSpot CRM",
        1000,
        "crm/v3/objects/contacts/{hubspot_contact_id}",
        "{resolved_values_json}",
        [{ name: "id", customName: "hubspot_sync_id", type: "string" }],
      ),

      // 6. Marketing platform sync
      n("6", "hitApi", "Sync to marketing automation platform", 1240, {
        hitApiDetails: {
          method: "PUT",
          url: "https://api.customer.io/v1/customers/{canonical_id}",
          headers: [
            { key: "Authorization", value: "Basic {customerio_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "email", value: "{customer_email}" },
            { key: "attributes", value: "{resolved_values_json}" },
          ],
          variables: [
            {
              name: "status",
              customName: "marketing_sync_status",
              type: "string",
            },
          ],
        },
      }),

      // 7. Data warehouse update (Sheets)
      sheetsNode(
        "7",
        "Update data warehouse log",
        1480,
        "CDPSyncLog",
        '[["{event_timestamp}","{canonical_id}","{customer_email}","{event_type}","{updated_fields}","{resolution_strategy}","{conflict_severity}","{hubspot_sync_id}","{marketing_sync_status}"]]',
      ),

      // 8. Sync failure handler condition
      n("8", "condition", "Did all syncs complete successfully?", 1720, {
        conditionData: [
          {
            variable: "identity_resolved",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
          {
            variable: "hubspot_sync_id",
            value: "",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 9. Audit log + Slack alert
      slackNode(
        "9",
        "Slack: CDP sync audit alert",
        1960,
        "CDP Sync Alert\\n\\nCustomer: {canonical_id} ({customer_email})\\nEvent: {event_type}\\nUpdated Fields: {updated_fields}\\nConflict: {has_conflict} (Severity: {conflict_severity})\\nResolution: {resolution_strategy}\\n\\nHubSpot: {hubspot_sync_id}\\nMarketing: {marketing_sync_status}\\nWarehouse: Logged\\n\\nAudit: {audit_note}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9", "output_1"), // all syncs OK -> audit log
      e("8", "9", "output_2"), // sync failure -> alert with error
    ],
  },

  // ===================================================================
  // 958. CUSTOMER JOURNEY ORCHESTRATOR (10 nodes)
  // ===================================================================

  "Customer Journey Orchestrator": {
    summary:
      "Orchestrates multi-channel customer journeys based on behavioral signals. Classifies journey stage with runCode, determines next-best-action via AI, dispatches email, SMS, push, and ad audience sync, routes journey branches with conditions, tracks conversion attribution in Sheets, and monitors journey health.",
    nodes: [
      trigger("Webhook: Behavioral signal listener", "webhook_app", {
        source: "analytics_platform",
        event: "user.behavior.signal",
        howItStarts:
          "A behavioral signal fires (page view, feature usage, cart action, email open, etc.) triggering journey orchestration to determine and execute the next-best-action across channels",
        setup:
          "Configure behavioral event webhook from analytics platform. Connect all channel APIs: email, Twilio SMS, OneSignal push, Meta Ads audiences. Set up journey stage definitions and transition rules. Connect Google Sheets for attribution tracking.",
        inputFields: [],
      }),

      // 2. AI extraction of behavioral signal
      aiNode(
        "2",
        "AI: Extract behavioral signal data",
        280,
        "You are a behavioral analytics processor. Parse the behavioral signal and extract user identity, action details, context, and journey-relevant metadata. Respond JSON.",
        "Parse this behavioral signal: {webhook_payload}. Extract user_id, user_email, user_phone, signal_type (pageview/click/purchase/abandon/engage), page_or_feature, session_count, days_since_signup, last_purchase_days_ago, cart_value, and previous_touchpoints.",
        [
          { field: "user_id", as: "user_id", type: "string" },
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_phone", as: "user_phone", type: "string" },
          { field: "signal_type", as: "signal_type", type: "string" },
          { field: "page_or_feature", as: "page_or_feature", type: "string" },
          { field: "session_count", as: "session_count", type: "number" },
          {
            field: "days_since_signup",
            as: "days_since_signup",
            type: "number",
          },
          {
            field: "last_purchase_days_ago",
            as: "last_purchase_days",
            type: "number",
          },
          { field: "cart_value", as: "cart_value", type: "string" },
          {
            field: "previous_touchpoints",
            as: "previous_touchpoints",
            type: "string",
          },
        ],
      ),

      // 3. Journey stage classifier (runCode)
      n("3", "runCode", "Classify customer journey stage", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify customer into journey stage based on behavioral signals
const daysSinceSignup = parseInt(vars.days_since_signup || "0");
const sessionCount = parseInt(vars.session_count || "0");
const lastPurchase = parseInt(vars.last_purchase_days || "999");
const signalType = vars.signal_type || "pageview";
const cartValue = parseFloat(vars.cart_value || "0");
let stage = "awareness";
let priority = "low";
if (signalType === "purchase") {
  stage = "retention";
  priority = "medium";
} else if (signalType === "abandon" && cartValue > 0) {
  stage = "decision";
  priority = "high";
} else if (sessionCount >= 5 && daysSinceSignup <= 14) {
  stage = "consideration";
  priority = "medium";
} else if (lastPurchase > 60) {
  stage = "winback";
  priority = "high";
} else if (daysSinceSignup <= 3) {
  stage = "onboarding";
  priority = "medium";
} else if (sessionCount >= 10) {
  stage = "engagement";
  priority = "low";
}
return {
  journey_stage: stage,
  stage_priority: priority,
  recommended_channels: stage === "decision" ? "email,sms,push" : stage === "winback" ? "email,sms" : "email,push",
  journey_context: stage + " stage, " + signalType + " signal, " + sessionCount + " sessions"
};`,
          outputVariables: [
            { name: "journey_stage", type: "string" },
            { name: "stage_priority", type: "string" },
            { name: "recommended_channels", type: "string" },
            { name: "journey_context", type: "string" },
          ],
        },
      }),

      // 4. Next-best-action engine (AI)
      aiNode(
        "4",
        "AI: Determine next-best-action",
        760,
        "You are a customer journey optimization engine. Based on the journey stage, behavioral signal, and context, determine the optimal next action across all channels. Personalize messaging for the specific stage. Respond JSON.",
        "User {user_id} is in {journey_stage} stage. Signal: {signal_type} on {page_or_feature}. Context: {journey_context}. Cart value: ${cart_value}. Previous touchpoints: {previous_touchpoints}. Determine next_action (description), email_content (subject + body), sms_content (max 160 chars), push_content (title + body), ad_audience_action (add/remove/none), and action_urgency (immediate/delayed/scheduled).",
        [
          { field: "next_action", as: "next_action", type: "string" },
          {
            field: "email_content",
            as: "journey_email_content",
            type: "string",
          },
          { field: "sms_content", as: "journey_sms_content", type: "string" },
          { field: "push_content", as: "journey_push_content", type: "string" },
          {
            field: "ad_audience_action",
            as: "ad_audience_action",
            type: "string",
          },
          { field: "action_urgency", as: "action_urgency", type: "string" },
        ],
      ),

      // 5. Email journey node
      emailNode(
        "5",
        "Email: Journey-stage message",
        1000,
        "{user_email}",
        "Your next step with {company_name}",
        "{journey_email_content}",
      ),

      // 6. SMS journey node (Twilio)
      smsNode(
        "6",
        "Twilio: Journey SMS touchpoint",
        1240,
        "{user_phone}",
        "{journey_sms_content}",
      ),

      // 7. Push notification (OneSignal)
      n(
        "7",
        "hitApi",
        "OneSignal: Journey push notification",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://onesignal.com/api/v1/notifications",
            headers: [
              { key: "Authorization", value: "Basic {onesignal_rest_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "app_id", value: "{onesignal_app_id}" },
              { key: "include_external_user_ids", value: '["{user_id}"]' },
              { key: "headings", value: '{"en":"Next step in your journey"}' },
              { key: "contents", value: '{"en":"{journey_push_content}"}' },
            ],
            variables: [
              { name: "id", customName: "journey_push_id", type: "string" },
              {
                name: "recipients",
                customName: "push_recipients",
                type: "number",
              },
            ],
          },
        },
        460,
      ),

      // 8. Journey branch router condition
      n("8", "condition", "Is journey stage high priority?", 1480, {
        conditionData: [
          {
            variable: "stage_priority",
            value: "high",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 9. Conversion attribution (Sheets)
      sheetsNode(
        "9",
        "Log conversion attribution data",
        1720,
        "JourneyAttribution",
        '[["{current_date}","{user_id}","{user_email}","{journey_stage}","{signal_type}","{page_or_feature}","{next_action}","{action_urgency}","{stage_priority}","{recommended_channels}"]]',
      ),

      // 10. Journey health monitor (Slack)
      slackNode(
        "10",
        "Slack: Journey health monitor",
        1960,
        "Journey Orchestration\\n\\nUser: {user_id}\\nStage: {journey_stage}\\nSignal: {signal_type} on {page_or_feature}\\nPriority: {stage_priority}\\nAction: {next_action}\\nUrgency: {action_urgency}\\nChannels: {recommended_channels}\\n\\nEmail + SMS + Push dispatched. Attribution logged.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9", "output_1"), // high priority -> log + monitor
      e("8", "10", "output_2"), // lower priority -> just monitor
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 959. CUSTOMER LIFETIME VALUE PREDICTOR (8 nodes)
  // ===================================================================

  "Customer Lifetime Value Predictor": {
    summary:
      "Predicts customer lifetime value using historical data aggregation, feature engineering via runCode, AI-powered LTV prediction, tier segmentation, CRM tag updates in HubSpot, high-value Slack alerts, marketing automation sync, and prediction accuracy monitoring in Sheets.",
    nodes: [
      trigger("Schedule: Historical data aggregation", "schedule", {
        schedule: "0 6 * * 1 (Every Monday at 6 AM)",
        howItStarts:
          "A weekly scheduled job aggregates customer transaction history, engagement metrics, and behavioral data to feed into the LTV prediction model",
        setup:
          "Configure data source connections for transaction history (CRM, payment processor). Set up HubSpot CRM for tag updates. Define LTV tier thresholds. Connect Slack for high-value alerts. Set up Google Sheets for accuracy monitoring.",
        inputFields: [],
      }),

      // 2. Feature engineering (runCode)
      n("2", "runCode", "Engineer features for LTV prediction", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Engineer features from customer data for LTV prediction
// In production, this would process batch customer data
const features = {
  avg_order_value: parseFloat("{avg_order_value}" || "0"),
  order_frequency: parseFloat("{order_frequency}" || "0"),
  days_as_customer: parseInt("{days_as_customer}" || "0"),
  total_orders: parseInt("{total_orders}" || "0"),
  total_revenue: parseFloat("{total_revenue}" || "0"),
  return_rate: parseFloat("{return_rate}" || "0"),
  email_engagement: parseFloat("{email_open_rate}" || "0"),
  support_tickets: parseInt("{support_tickets}" || "0"),
  referrals_made: parseInt("{referrals_made}" || "0"),
};
const recency = parseInt("{days_since_last_order}" || "30");
const frequency = features.order_frequency;
const monetary = features.avg_order_value;
const rfmScore = (
  (recency < 30 ? 5 : recency < 60 ? 4 : recency < 90 ? 3 : recency < 180 ? 2 : 1) +
  (frequency > 12 ? 5 : frequency > 6 ? 4 : frequency > 3 ? 3 : frequency > 1 ? 2 : 1) +
  (monetary > 200 ? 5 : monetary > 100 ? 4 : monetary > 50 ? 3 : monetary > 25 ? 2 : 1)
) / 3;
return {
  features_json: JSON.stringify(features),
  rfm_score: rfmScore.toFixed(2),
  recency_days: recency,
  frequency_score: frequency.toFixed(2),
  monetary_score: monetary.toFixed(2),
  customer_tenure_days: features.days_as_customer,
  feature_count: Object.keys(features).length,
  feature_engineering_complete: "true"
};`,
          outputVariables: [
            { name: "features_json", type: "string" },
            { name: "rfm_score", type: "string" },
            { name: "recency_days", type: "number" },
            { name: "frequency_score", type: "string" },
            { name: "monetary_score", type: "string" },
            { name: "customer_tenure_days", type: "number" },
            { name: "feature_count", type: "number" },
            { name: "feature_engineering_complete", type: "string" },
          ],
        },
      }),

      // 3. AI LTV prediction
      aiNode(
        "3",
        "AI: Predict customer lifetime value",
        520,
        "You are a customer analytics data scientist. Given customer features and RFM scores, predict the customer lifetime value for the next 12 months. Consider purchase patterns, engagement trends, and churn risk. Respond JSON.",
        "Predict 12-month LTV. Features: {features_json}. RFM Score: {rfm_score}. Recency: {recency_days} days. Frequency: {frequency_score}. Monetary: ${monetary_score}. Tenure: {customer_tenure_days} days. Return predicted_ltv_12m (dollar amount), confidence_interval (low-high range), churn_probability (0-100), growth_trajectory (increasing/stable/declining), and prediction_factors (key drivers).",
        [
          { field: "predicted_ltv_12m", as: "predicted_ltv", type: "string" },
          {
            field: "confidence_interval",
            as: "ltv_confidence_range",
            type: "string",
          },
          {
            field: "churn_probability",
            as: "churn_probability",
            type: "string",
          },
          {
            field: "growth_trajectory",
            as: "growth_trajectory",
            type: "string",
          },
          {
            field: "prediction_factors",
            as: "prediction_factors",
            type: "string",
          },
        ],
      ),

      // 4. Tier segmentation (runCode)
      n("4", "runCode", "Segment customer into LTV tier", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Segment customer into LTV tier based on prediction
const ltv = parseFloat(vars.predicted_ltv || "0");
const churnProb = parseFloat(vars.churn_probability || "50");
let tier = "Standard";
let tierTag = "ltv_standard";
let alertNeeded = "no";
if (ltv >= 1000 && churnProb < 30) {
  tier = "Platinum";
  tierTag = "ltv_platinum";
  alertNeeded = "yes";
} else if (ltv >= 500) {
  tier = "Gold";
  tierTag = "ltv_gold";
  alertNeeded = ltv >= 750 ? "yes" : "no";
} else if (ltv >= 200) {
  tier = "Silver";
  tierTag = "ltv_silver";
}
const atRisk = churnProb > 60 && ltv >= 200;
return {
  ltv_tier: tier,
  ltv_tier_tag: tierTag,
  alert_needed: alertNeeded,
  at_risk_high_value: atRisk ? "yes" : "no",
  tier_description: tier + " tier ($" + ltv.toFixed(0) + " predicted LTV, " + churnProb + "% churn risk)"
};`,
          outputVariables: [
            { name: "ltv_tier", type: "string" },
            { name: "ltv_tier_tag", type: "string" },
            { name: "alert_needed", type: "string" },
            { name: "at_risk_high_value", type: "string" },
            { name: "tier_description", type: "string" },
          ],
        },
      }),

      // 5. CRM tag update (HubSpot)
      hubspotUpdateNode(
        "5",
        "Update HubSpot CRM with LTV tier",
        1000,
        "crm/v3/objects/contacts/{hubspot_contact_id}",
        '{"ltv_tier":"{ltv_tier}","predicted_ltv_12m":"{predicted_ltv}","churn_probability":"{churn_probability}","growth_trajectory":"{growth_trajectory}","ltv_last_updated":"{current_date}","rfm_score":"{rfm_score}"}',
        [{ name: "id", customName: "hubspot_update_id", type: "string" }],
      ),

      // 6. High-value Slack alert
      slackNode(
        "6",
        "Slack: High-value customer alert",
        1240,
        "LTV Prediction Alert\\n\\nTier: {ltv_tier}\\nPredicted 12M LTV: ${predicted_ltv}\\nConfidence: {ltv_confidence_range}\\nChurn Risk: {churn_probability}%\\nTrajectory: {growth_trajectory}\\nAt-Risk High-Value: {at_risk_high_value}\\n\\nRFM Score: {rfm_score}\\nKey Factors: {prediction_factors}\\n\\nCRM updated with {ltv_tier_tag} tag.",
      ),

      // 7. Marketing automation sync
      n("7", "hitApi", "Sync LTV tier to marketing automation", 1480, {
        hitApiDetails: {
          method: "PUT",
          url: "https://api.customer.io/v1/customers/{customer_id}",
          headers: [
            { key: "Authorization", value: "Basic {customerio_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "ltv_tier", value: "{ltv_tier}" },
            { key: "predicted_ltv", value: "{predicted_ltv}" },
            { key: "churn_risk", value: "{churn_probability}" },
          ],
          variables: [
            {
              name: "status",
              customName: "marketing_sync_status",
              type: "string",
            },
          ],
        },
      }),

      // 8. Prediction accuracy monitor (Sheets)
      sheetsNode(
        "8",
        "Log LTV prediction for accuracy tracking",
        1720,
        "LTVPredictions",
        '[["{current_date}","{customer_id}","{ltv_tier}","{predicted_ltv}","{ltv_confidence_range}","{churn_probability}","{growth_trajectory}","{rfm_score}","{prediction_factors}","{at_risk_high_value}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 960. CUSTOMER SUPPORT VIDEO RESPONSE (8 nodes)
  // ===================================================================

  "Customer Support Video Response": {
    summary:
      "Creates personalized video responses for support tickets. A ticket trigger fires, AI extracts context, generates a video script, renders the video via API, a quality review condition gates delivery, the video is uploaded to hosting, a reply is posted to Zendesk, and errors are reported to Slack.",
    nodes: [
      trigger("Webhook: Support ticket trigger", "webhook_app", {
        source: "zendesk",
        event: "ticket.created",
        howItStarts:
          "A new support ticket is created in Zendesk that meets video response criteria (VIP customer, complex issue, or escalation), triggering personalized video response generation",
        setup:
          "Configure Zendesk ticket webhook filtered for video-eligible tickets. Connect OpenAI for script generation. Set up video rendering API (Synthesia or similar). Configure video hosting (Mux or Cloudinary). Connect Slack for error notifications.",
        inputFields: [],
      }),

      // 2. Ticket context extraction (AI)
      aiNode(
        "2",
        "AI: Extract ticket context and customer details",
        280,
        "You are a customer support analyst. Parse the support ticket webhook and extract all relevant information for creating a personalized video response. Understand the customer's issue, emotional tone, and urgency. Respond JSON.",
        "Parse this support ticket: {webhook_payload}. Extract ticket_id, customer_name, customer_email, issue_summary, issue_category, sentiment (positive/neutral/negative/frustrated), urgency_level (low/medium/high/critical), previous_tickets_count, customer_tier, and resolution_steps (brief outline of how to resolve).",
        [
          { field: "ticket_id", as: "ticket_id", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "issue_summary", as: "issue_summary", type: "string" },
          { field: "issue_category", as: "issue_category", type: "string" },
          { field: "sentiment", as: "customer_sentiment", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          { field: "customer_tier", as: "customer_tier", type: "string" },
          { field: "resolution_steps", as: "resolution_steps", type: "string" },
        ],
      ),

      // 3. Video script generation
      aiNode(
        "3",
        "AI: Generate video response script",
        520,
        "You are a customer support video scriptwriter. Write a warm, professional video script that addresses the customer by name, acknowledges their issue with empathy, and walks through the resolution step by step. Keep it under 2 minutes (approximately 250 words). Adapt tone based on customer sentiment. Respond JSON.",
        "Write a video response script for {customer_name}. Issue: {issue_summary}. Category: {issue_category}. Sentiment: {customer_sentiment}. Urgency: {urgency_level}. Tier: {customer_tier}. Resolution steps: {resolution_steps}. Return video_script (full narration text), script_duration_estimate (seconds), intro_line (warm greeting), closing_line (reassuring outro), and key_visual_cues (comma-separated list of things to show on screen).",
        [
          { field: "video_script", as: "video_script", type: "string" },
          {
            field: "script_duration_estimate",
            as: "video_duration_secs",
            type: "number",
          },
          { field: "intro_line", as: "video_intro", type: "string" },
          { field: "closing_line", as: "video_closing", type: "string" },
          { field: "key_visual_cues", as: "visual_cues", type: "string" },
        ],
      ),

      // 4. Video rendering
      n("4", "hitApi", "Render personalized video (Synthesia)", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.synthesia.io/v2/videos",
          headers: [
            { key: "Authorization", value: "Bearer {synthesia_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "Support Response - Ticket #{ticket_id}" },
            {
              key: "description",
              value: "Personalized video response for {customer_name}",
            },
            {
              key: "input",
              value:
                '[{"scriptText":"{video_script}","avatar":"anna_costume1_cameraA","background":"off_white"}]',
            },
            { key: "test", value: "false" },
          ],
          variables: [
            { name: "id", customName: "video_render_id", type: "string" },
            {
              name: "status",
              customName: "video_render_status",
              type: "string",
            },
          ],
        },
      }),

      // 5. Quality review condition
      n("5", "condition", "Video render completed successfully?", 1000, {
        conditionData: [
          {
            variable: "video_render_id",
            value: "",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Video hosting upload
      n("6", "hitApi", "Retrieve video URL from hosting", 1240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.synthesia.io/v2/videos/{video_render_id}",
          headers: [
            { key: "Authorization", value: "Bearer {synthesia_api_key}" },
          ],
          body: [],
          variables: [
            {
              name: "download",
              customName: "video_download_url",
              type: "string",
            },
            {
              name: "status",
              customName: "video_final_status",
              type: "string",
            },
            {
              name: "duration",
              customName: "video_actual_duration",
              type: "string",
            },
          ],
        },
      }),

      // 7. Customer reply (Zendesk)
      n("7", "hitApi", "Post video reply to Zendesk ticket", 1480, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{zendesk_subdomain}.zendesk.com/api/v2/tickets/{ticket_id}.json",
          headers: [
            { key: "Authorization", value: "Basic {zendesk_api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "ticket",
              value: `{"comment":{"body":"Hi {customer_name},\\n\\nThank you for reaching out. We've prepared a personalized video response to help resolve your issue.\\n\\nWatch your video response: {video_download_url}\\n\\n{video_closing}\\n\\nIf you need any further assistance, please don't hesitate to reply to this ticket.","public":true},"status":"pending"}`,
            },
          ],
          variables: [
            {
              name: "ticket.id",
              customName: "zendesk_ticket_updated",
              type: "string",
            },
          ],
        },
      }),

      // 8. Delivery error handler (Slack)
      slackNode(
        "8",
        "Slack: Video response delivery report",
        1720,
        "Support Video Response\\n\\nTicket: #{ticket_id}\\nCustomer: {customer_name} ({customer_tier})\\nIssue: {issue_summary}\\nSentiment: {customer_sentiment}\\nUrgency: {urgency_level}\\n\\nVideo: {video_download_url}\\nDuration: {video_actual_duration}s\\nRender Status: {video_final_status}\\n\\nZendesk ticket updated with video response.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // render success -> get URL
      e("5", "8", "output_2"), // render failed -> error to Slack
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 961. CUSTOMER TESTIMONIAL COLLECTOR (8 nodes)
  // ===================================================================

  "Customer Testimonial Collector": {
    summary:
      "Collects testimonials from multiple sources, deduplicates with runCode, analyzes quality and authenticity with AI, filters with a condition, categorizes approved testimonials, stores them in Airtable, notifies marketing via Slack, and sends a permission request email to the customer.",
    nodes: [
      trigger("Webhook: Multi-source testimonial listener", "webhook_app", {
        source: "review_platform",
        event: "testimonial.received",
        howItStarts:
          "A new testimonial is detected from any connected source (review platform, NPS survey, social media mention, support ticket praise, or direct submission) and routed into the collection pipeline",
        setup:
          "Configure webhooks from review platforms (G2, Trustpilot, Capterra), NPS tools, and social listening. Connect OpenAI for quality analysis. Set up Airtable for testimonial repository. Configure Slack for marketing notifications and email for permission requests.",
        inputFields: [],
      }),

      // 2. AI extraction
      aiNode(
        "2",
        "AI: Extract testimonial data",
        280,
        "You are a testimonial data processor. Parse the incoming testimonial from any source format and extract structured data. Handle variations in field names and formats. Respond JSON.",
        "Parse this testimonial: {webhook_payload}. Extract testimonial_text, author_name, author_email, author_company, author_title, source_platform, rating (1-5 if available), product_mentioned, date_submitted, and source_url. Find each field regardless of payload format.",
        [
          { field: "testimonial_text", as: "testimonial_text", type: "string" },
          { field: "author_name", as: "author_name", type: "string" },
          { field: "author_email", as: "author_email", type: "string" },
          { field: "author_company", as: "author_company", type: "string" },
          { field: "author_title", as: "author_title", type: "string" },
          { field: "source_platform", as: "source_platform", type: "string" },
          { field: "rating", as: "rating", type: "number" },
          {
            field: "product_mentioned",
            as: "product_mentioned",
            type: "string",
          },
          { field: "date_submitted", as: "date_submitted", type: "string" },
          { field: "source_url", as: "source_url", type: "string" },
        ],
      ),

      // 3. Dedup engine (runCode)
      n("3", "runCode", "Deduplicate and fingerprint testimonial", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate a fingerprint for deduplication
const text = (vars.testimonial_text || "").toLowerCase().trim();
const author = (vars.author_name || "").toLowerCase().trim();
const source = vars.source_platform || "unknown";
// Simple hash for dedup
let hash = 0;
const sample = (text + author).slice(0, 200);
for (let i = 0; i < sample.length; i++) {
  const char = sample.charCodeAt(i);
  hash = ((hash << 5) - hash) + char;
  hash = hash & hash;
}
const fingerprint = source + "_" + Math.abs(hash).toString(36);
const wordCount = text.split(/\\s+/).length;
return {
  testimonial_fingerprint: fingerprint,
  word_count: wordCount,
  is_substantial: wordCount >= 10 ? "yes" : "no",
  dedup_key: fingerprint,
  text_normalized: text.slice(0, 500)
};`,
          outputVariables: [
            { name: "testimonial_fingerprint", type: "string" },
            { name: "word_count", type: "number" },
            { name: "is_substantial", type: "string" },
            { name: "dedup_key", type: "string" },
            { name: "text_normalized", type: "string" },
          ],
        },
      }),

      // 4. AI content analyzer
      aiNode(
        "4",
        "AI: Analyze quality and authenticity",
        760,
        "You are a testimonial quality analyst. Evaluate the testimonial for authenticity, marketing value, emotional impact, specificity, and usability. Flag anything that looks fake, generic, or unusable. Respond JSON.",
        "Analyze this testimonial: {testimonial_text}. Author: {author_name}, {author_title} at {author_company}. Source: {source_platform}. Rating: {rating}. Return quality_score (1-10), authenticity_score (1-10), marketing_value (low/medium/high), key_quote (best quotable excerpt), emotional_tone (positive/neutral/mixed), specificity_level (vague/moderate/specific), suggested_use_case (website/social/case-study/ads), and flag_reason (empty if no issues).",
        [
          { field: "quality_score", as: "quality_score", type: "number" },
          {
            field: "authenticity_score",
            as: "authenticity_score",
            type: "number",
          },
          { field: "marketing_value", as: "marketing_value", type: "string" },
          { field: "key_quote", as: "key_quote", type: "string" },
          { field: "emotional_tone", as: "emotional_tone", type: "string" },
          {
            field: "specificity_level",
            as: "specificity_level",
            type: "string",
          },
          {
            field: "suggested_use_case",
            as: "suggested_use_case",
            type: "string",
          },
          { field: "flag_reason", as: "flag_reason", type: "string" },
        ],
      ),

      // 5. Quality + authenticity filter condition
      n("5", "condition", "Testimonial passes quality threshold?", 1000, {
        conditionData: [
          {
            variable: "quality_score",
            value: "6",
            comparison_operator: "GREATER THAN OR EQUAL TO",
            logical_operator: "AND",
          },
          {
            variable: "authenticity_score",
            value: "6",
            comparison_operator: "GREATER THAN OR EQUAL TO",
            logical_operator: "AND",
          },
          {
            variable: "is_substantial",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Airtable repository storage
      n("6", "hitApi", "Store testimonial in Airtable", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.airtable.com/v0/{airtable_base_id}/Testimonials",
          headers: [
            { key: "Authorization", value: "Bearer {airtable_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "records",
              value:
                '[{"fields":{"Testimonial":"{testimonial_text}","Author":"{author_name}","Company":"{author_company}","Title":"{author_title}","Email":"{author_email}","Source":"{source_platform}","Rating":{rating},"Quality Score":{quality_score},"Marketing Value":"{marketing_value}","Key Quote":"{key_quote}","Use Case":"{suggested_use_case}","Fingerprint":"{testimonial_fingerprint}","Permission Status":"Pending","Date":"{date_submitted}"}}]',
            },
          ],
          variables: [
            {
              name: "records[0].id",
              customName: "airtable_record_id",
              type: "string",
            },
          ],
        },
      }),

      // 7. Marketing Slack notification
      slackNode(
        "7",
        "Slack: New quality testimonial alert",
        1480,
        'New Testimonial Collected\\n\\nAuthor: {author_name}, {author_title} at {author_company}\\nSource: {source_platform}\\nRating: {rating}/5\\nQuality: {quality_score}/10\\nMarketing Value: {marketing_value}\\n\\nKey Quote: \\"{key_quote}\\"\\n\\nSuggested Use: {suggested_use_case}\\nTone: {emotional_tone}\\nSpecificity: {specificity_level}\\n\\nStored in Airtable. Permission request sent to author.',
      ),

      // 8. Permission request email
      emailNode(
        "8",
        "Email: Request testimonial usage permission",
        1720,
        "{author_email}",
        "Permission to feature your testimonial - {company_name}",
        'Hi {author_name},\\n\\nThank you for your kind words about {company_name}! We were thrilled to read your {source_platform} review.\\n\\nWe would love to feature your testimonial on our {suggested_use_case}. Here is the quote we would like to use:\\n\\n\\"{key_quote}\\"\\n\\n- {author_name}, {author_title} at {author_company}\\n\\nPlease reply with your approval, or let us know if you would like us to adjust anything.\\n\\nThank you for being a valued customer!\\n\\nBest regards,\\n{company_name} Marketing Team',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // passes quality -> store + notify
      e("5", "7", "output_2"), // fails quality -> still notify (with flag)
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 962. CUSTOMER TESTIMONIAL SOCIAL POST (7 nodes)
  // ===================================================================

  "Customer Testimonial Social Post": {
    summary:
      "Transforms approved testimonials into engaging social media posts. An approved testimonial trigger fires, customer context is enriched from HubSpot, AI composes platform-optimized social posts, DALL-E generates a visual asset, a compliance approval gate reviews the content, approved posts are published via Buffer, and engagement is tracked in Sheets.",
    nodes: [
      trigger("Webhook: Approved testimonial trigger", "webhook_app", {
        source: "airtable",
        event: "testimonial.permission.approved",
        howItStarts:
          "A customer replies with permission to use their testimonial, updating the Airtable record and triggering social post creation",
        setup:
          "Configure Airtable webhook for permission status changes. Connect HubSpot for customer enrichment. Set up DALL-E for visual generation. Configure Buffer for multi-platform publishing. Connect Google Sheets for engagement tracking.",
        inputFields: [],
      }),

      // 2. Context enrichment (HubSpot)
      aiNode(
        "2",
        "AI: Extract testimonial and enrich context",
        280,
        "You are a testimonial data processor. Parse the approval webhook and extract the testimonial details for social content creation. Respond JSON.",
        "Parse this testimonial approval: {webhook_payload}. Extract testimonial_text, author_name, author_company, author_title, key_quote, suggested_use_case, source_platform, rating, product_mentioned, and customer_tier. Find each field regardless of format.",
        [
          { field: "testimonial_text", as: "testimonial_text", type: "string" },
          { field: "author_name", as: "author_name", type: "string" },
          { field: "author_company", as: "author_company", type: "string" },
          { field: "author_title", as: "author_title", type: "string" },
          { field: "key_quote", as: "key_quote", type: "string" },
          { field: "source_platform", as: "source_platform", type: "string" },
          { field: "rating", as: "rating", type: "string" },
          {
            field: "product_mentioned",
            as: "product_mentioned",
            type: "string",
          },
          { field: "customer_tier", as: "customer_tier", type: "string" },
        ],
      ),

      // 3. AI social post composer
      aiNode(
        "3",
        "AI: Compose social media posts from testimonial",
        520,
        "You are a social media content creator specializing in testimonial amplification. Create engaging social posts that highlight the customer's success story. Each platform version should feel native. Respond JSON.",
        'Create social posts from this testimonial. Quote: \\"{key_quote}\\" - {author_name}, {author_title} at {author_company}. Product: {product_mentioned}. Rating: {rating}/5. Return twitter_post (max 280 chars with attribution), linkedin_post (professional format, 100-200 words), instagram_caption (storytelling with emojis), facebook_post (conversational), social_hashtags (5-10 relevant tags), and visual_description (for image generation).',
        [
          { field: "twitter_post", as: "twitter_post", type: "string" },
          { field: "linkedin_post", as: "linkedin_post", type: "string" },
          {
            field: "instagram_caption",
            as: "instagram_caption",
            type: "string",
          },
          { field: "facebook_post", as: "facebook_post", type: "string" },
          { field: "social_hashtags", as: "social_hashtags", type: "string" },
          {
            field: "visual_description",
            as: "visual_description",
            type: "string",
          },
        ],
      ),

      // 4. Visual asset generator (DALL-E)
      n("4", "hitApi", "Generate testimonial visual (DALL-E)", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/images/generations",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "dall-e-3" },
            {
              key: "prompt",
              value:
                "Professional social media testimonial graphic. {visual_description}. Clean modern design with quote-style layout, warm brand colors, abstract background elements suggesting trust and success. No text overlay, no faces. 1080x1080 square.",
            },
            { key: "size", value: "1024x1024" },
            { key: "quality", value: "standard" },
          ],
          variables: [
            {
              name: "data[0].url",
              customName: "testimonial_image_url",
              type: "string",
            },
            {
              name: "data[0].revised_prompt",
              customName: "image_prompt_revised",
              type: "string",
            },
          ],
        },
      }),

      // 5. Compliance approval gate
      n("5", "approvalGate", "Approval: Testimonial social content", 1000, {
        approvalGateData: {
          message:
            'Testimonial Social Post Approval\\n\\nCustomer: {author_name}, {author_title} at {author_company}\\nQuote: \\"{key_quote}\\"\\n\\nTwitter/X:\\n{twitter_post}\\n\\nLinkedIn:\\n{linkedin_post}\\n\\nInstagram:\\n{instagram_caption}\\n\\nFacebook:\\n{facebook_post}\\n\\nHashtags: {social_hashtags}\\nImage: {testimonial_image_url}\\n\\nApprove to schedule publishing across all platforms?',
        },
      }),

      // 6. Multi-platform publisher (Buffer)
      bufferNode(
        "6",
        "Buffer: Publish testimonial posts",
        1240,
        "{linkedin_post} {social_hashtags}",
      ),

      // 7. Engagement monitor (Sheets)
      sheetsNode(
        "7",
        "Log testimonial social campaign",
        1480,
        "TestimonialSocial",
        '[["{current_date}","{author_name}","{author_company}","{key_quote}","{source_platform}","{rating}","{social_hashtags}","{testimonial_image_url}","{product_mentioned}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // approved -> publish
      e("5", "7", "output_2"), // rejected -> still log
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 963. CUSTOMER WIN-BACK CAMPAIGN (8 nodes)
  // ===================================================================

  "Customer Win-Back Campaign": {
    summary:
      "Runs a weekly churn scan, scores risk with runCode, generates an AI win-back email, selects incentive tiers, dispatches the email, monitors response with a delay and condition, escalates non-responders to SMS via Twilio, and logs campaign results to Sheets.",
    nodes: [
      trigger("Schedule: Weekly churn scan", "schedule", {
        schedule: "0 9 * * 2 (Every Tuesday at 9 AM)",
        howItStarts:
          "A weekly scheduled scan identifies customers showing churn signals (declining usage, support complaints, failed renewals) and initiates personalized win-back campaigns",
        setup:
          "Configure customer data source for churn signal detection. Set churn risk thresholds. Connect Twilio for SMS escalation. Set up Google Sheets for campaign results tracking. Define incentive tiers by customer value.",
        inputFields: [],
      }),

      // 2. Churn risk scorer (runCode)
      n("2", "runCode", "Score customer churn risk", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score customer churn risk based on behavioral signals
const daysSinceLogin = parseInt("{days_since_last_login}" || "30");
const daysSincePurchase = parseInt("{days_since_last_purchase}" || "60");
const supportTickets = parseInt("{recent_support_tickets}" || "0");
const usageTrend = "{usage_trend}" || "stable";
const paymentFailures = parseInt("{payment_failures}" || "0");
let riskScore = 0;
riskScore += daysSinceLogin > 30 ? 25 : daysSinceLogin > 14 ? 15 : 5;
riskScore += daysSincePurchase > 90 ? 30 : daysSincePurchase > 60 ? 20 : 5;
riskScore += supportTickets > 3 ? 20 : supportTickets > 1 ? 10 : 0;
riskScore += usageTrend === "declining" ? 15 : usageTrend === "stable" ? 5 : 0;
riskScore += paymentFailures > 0 ? 10 : 0;
const riskLevel = riskScore >= 70 ? "critical" : riskScore >= 50 ? "high" : riskScore >= 30 ? "moderate" : "low";
const ltv = parseFloat("{customer_ltv}" || "100");
let incentiveTier = "standard";
let incentiveOffer = "10% off next purchase";
if (ltv >= 500 && riskScore >= 50) {
  incentiveTier = "premium";
  incentiveOffer = "25% off + free shipping + bonus gift";
} else if (ltv >= 200 || riskScore >= 70) {
  incentiveTier = "enhanced";
  incentiveOffer = "20% off + free shipping";
}
return {
  churn_risk_score: riskScore,
  risk_level: riskLevel,
  incentive_tier: incentiveTier,
  incentive_offer: incentiveOffer,
  customer_name: "{customer_name}",
  customer_email: "{customer_email}",
  customer_phone: "{customer_phone}",
  customer_ltv: ltv.toFixed(2),
  primary_risk_factor: daysSincePurchase > 60 ? "purchase_lapse" : supportTickets > 3 ? "support_issues" : "declining_usage"
};`,
          outputVariables: [
            { name: "churn_risk_score", type: "number" },
            { name: "risk_level", type: "string" },
            { name: "incentive_tier", type: "string" },
            { name: "incentive_offer", type: "string" },
            { name: "customer_name", type: "string" },
            { name: "customer_email", type: "string" },
            { name: "customer_phone", type: "string" },
            { name: "customer_ltv", type: "string" },
            { name: "primary_risk_factor", type: "string" },
          ],
        },
      }),

      // 3. AI win-back email
      aiNode(
        "3",
        "AI: Compose win-back email",
        520,
        "You are a customer retention specialist. Write a personalized win-back email that acknowledges the customer's situation without being pushy, offers a compelling incentive, and creates a sense of care rather than urgency. Adapt tone based on risk level. Respond JSON.",
        "Write a win-back email for {customer_name}. Risk level: {risk_level} (score: {churn_risk_score}). LTV: ${customer_ltv}. Primary risk factor: {primary_risk_factor}. Incentive: {incentive_offer}. Return email_subject, email_body (warm, personal, with incentive and CTA), and winback_code (unique discount code suggestion).",
        [
          {
            field: "email_subject",
            as: "winback_email_subject",
            type: "string",
          },
          { field: "email_body", as: "winback_email_body", type: "string" },
          {
            field: "winback_code",
            as: "winback_discount_code",
            type: "string",
          },
        ],
      ),

      // 4. Incentive tier selector (uses runCode output)
      emailNode(
        "4",
        "Email: Send win-back campaign",
        760,
        "{customer_email}",
        "{winback_email_subject}",
        "Hi {customer_name},\\n\\n{winback_email_body}\\n\\nYour exclusive code: {winback_discount_code}\\n\\nWe hope to see you back soon!\\n{company_name}",
      ),

      // 5. Response monitor (delay)
      n("5", "delayAction", "Wait 72 hours for response", 1000, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "72",
          delayUnit: "hours",
          description:
            "Waiting 72 hours to check if the customer engaged with the win-back email before escalating to SMS",
        },
      }),

      // 6. Response condition
      n("6", "condition", "Customer at high risk and has phone?", 1240, {
        conditionData: [
          {
            variable: "risk_level",
            value: "low",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
          {
            variable: "customer_phone",
            value: "",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Non-responder SMS escalation (Twilio)
      smsNode(
        "7",
        "Twilio: Win-back SMS escalation",
        1480,
        "{customer_phone}",
        "Hi {customer_name}, we miss you at {company_name}! Use code {winback_discount_code} for {incentive_offer}. This offer expires soon. Shop now or reply STOP to opt out.",
      ),

      // 8. Campaign results logger (Sheets)
      sheetsNode(
        "8",
        "Log win-back campaign results",
        1720,
        "WinbackCampaign",
        '[["{current_date}","{customer_name}","{customer_email}","{churn_risk_score}","{risk_level}","{customer_ltv}","{incentive_tier}","{incentive_offer}","{winback_discount_code}","{primary_risk_factor}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // high risk + has phone -> SMS escalation
      e("6", "8", "output_2"), // low risk or no phone -> just log
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 964. CUSTOMER WINBACK AFTER CANCELLATION (9 nodes)
  // ===================================================================

  "Customer Winback After Cancellation": {
    summary:
      "Activates when a customer cancels their subscription. AI analyzes the cancellation reason, a condition routes the win-back strategy, AI generates a personalized email, dispatches it, waits for engagement, follows up with SMS for non-responders, sends a final AI follow-up email, and tracks all outcomes in Sheets.",
    nodes: [
      trigger("Webhook: Cancellation event trigger", "webhook_app", {
        source: "billing_platform",
        event: "subscription.cancelled",
        howItStarts:
          "A customer cancels their subscription or membership, triggering an immediate win-back sequence tailored to their cancellation reason",
        setup:
          "Configure billing platform webhook for cancellation events (Stripe, Chargebee, etc). Connect OpenAI for personalized messaging. Set up Twilio for SMS re-engagement. Connect Google Sheets for outcome tracking. Define win-back strategy matrix by cancellation reason.",
        inputFields: [],
      }),

      // 2. Cancellation reason analyzer (AI)
      aiNode(
        "2",
        "AI: Analyze cancellation reason and context",
        280,
        "You are a customer retention analyst. Parse the cancellation event and deeply analyze the reason, customer history, and potential win-back approaches. Respond JSON.",
        "Parse this cancellation event: {webhook_payload}. Extract customer_id, customer_name, customer_email, customer_phone, plan_name, monthly_amount, cancellation_reason, tenure_months, lifetime_spend, last_usage_date, and satisfaction_score. Also generate win_back_approach (best strategy based on reason), empathy_note (acknowledgment of their concern), and save_offer (what could bring them back).",
        [
          { field: "customer_id", as: "customer_id", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_phone", as: "customer_phone", type: "string" },
          { field: "plan_name", as: "plan_name", type: "string" },
          { field: "monthly_amount", as: "monthly_amount", type: "string" },
          {
            field: "cancellation_reason",
            as: "cancellation_reason",
            type: "string",
          },
          { field: "tenure_months", as: "tenure_months", type: "number" },
          { field: "lifetime_spend", as: "lifetime_spend", type: "string" },
          {
            field: "win_back_approach",
            as: "win_back_approach",
            type: "string",
          },
          { field: "empathy_note", as: "empathy_note", type: "string" },
          { field: "save_offer", as: "save_offer", type: "string" },
        ],
      ),

      // 3. Win-back strategy router condition
      n("3", "condition", "Is customer high-value and saveable?", 520, {
        conditionData: [
          {
            variable: "tenure_months",
            value: "3",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
          {
            variable: "cancellation_reason",
            value: "fraud",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI email #1
      aiNode(
        "4",
        "AI: Generate personalized win-back email #1",
        760,
        "You are a compassionate customer retention writer. Write a win-back email that shows genuine understanding of why they cancelled, does NOT sound desperate, and presents a compelling reason to return. Lead with empathy, not discounts. Respond JSON.",
        "Write win-back email #1 for {customer_name} who cancelled {plan_name} (${monthly_amount}/mo) after {tenure_months} months. Reason: {cancellation_reason}. Approach: {win_back_approach}. Empathy: {empathy_note}. Offer: {save_offer}. Return email_subject_1, email_body_1 (empathetic, personal, with soft CTA), and reactivation_url.",
        [
          { field: "email_subject_1", as: "winback_subject_1", type: "string" },
          { field: "email_body_1", as: "winback_body_1", type: "string" },
          { field: "reactivation_url", as: "reactivation_url", type: "string" },
        ],
      ),

      // 5. Email dispatch
      emailNode(
        "5",
        "Email: Win-back touch 1",
        1000,
        "{customer_email}",
        "{winback_subject_1}",
        "Hi {customer_name},\\n\\n{winback_body_1}\\n\\nIf you change your mind: {reactivation_url}\\n\\nWe're here if you need us.\\n{company_name}",
      ),

      // 6. Engagement wait condition
      n("6", "delayAction", "Wait 5 days for engagement", 1240, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "5",
          delayUnit: "days",
          description:
            "Waiting 5 days after the first win-back email before escalating to SMS re-engagement for non-responders",
        },
      }),

      // 7. SMS re-engagement (Twilio)
      smsNode(
        "7",
        "Twilio: Win-back SMS re-engagement",
        1480,
        "{customer_phone}",
        "Hi {customer_name}, we noticed you cancelled your {plan_name} plan. {save_offer}. Reactivate anytime: {reactivation_url} Reply STOP to opt out.",
      ),

      // 8. AI follow-up email #2
      aiNode(
        "8",
        "AI: Generate follow-up email #2",
        1720,
        "You are writing a final win-back email (touch 2). This should feel like a genuine last reach-out, not a hard sell. Share what's new, remind them of what they're missing, and make the return effortless. Respond JSON.",
        "Write final win-back email for {customer_name}. They cancelled {plan_name} because: {cancellation_reason}. Tenure: {tenure_months} months. Lifetime spend: ${lifetime_spend}. Save offer: {save_offer}. Return email_subject_2 and email_body_2 (final, warm, what's-new focused).",
        [
          { field: "email_subject_2", as: "winback_subject_2", type: "string" },
          { field: "email_body_2", as: "winback_body_2", type: "string" },
        ],
      ),

      // 9. Win-back outcome tracker (Sheets)
      sheetsNode(
        "9",
        "Log win-back outcomes",
        1960,
        "CancellationWinback",
        '[["{current_date}","{customer_id}","{customer_name}","{plan_name}","{monthly_amount}","{cancellation_reason}","{tenure_months}","{lifetime_spend}","{win_back_approach}","{save_offer}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // high-value + saveable -> full sequence
      e("3", "9", "output_2"), // not saveable -> just log
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 965. DISCOUNT CODE DELIVERY WELCOME (6 nodes)
  // ===================================================================

  "Discount Code Delivery Welcome": {
    summary:
      "Welcomes new signups with a unique discount code. Generates a unique code via runCode, AI composes a welcome email with the discount, dispatches it, waits with a delay before checking redemption via condition, and logs redemption tracking and cleanup to Sheets.",
    nodes: [
      trigger("Webhook: New signup trigger", "webhook_app", {
        source: "signup_form",
        event: "user.signup.completed",
        howItStarts:
          "A new user signs up via website, landing page, or checkout opt-in, triggering immediate welcome email delivery with a unique discount code",
        setup:
          "Configure signup webhook. Set discount parameters (percentage, minimum order, expiry). Connect Google Sheets for redemption tracking and code cleanup.",
        inputFields: [],
      }),

      // 2. Unique discount code generation (runCode)
      aiNode(
        "1b",
        "AI: Extract signup data",
        180,
        "You are a signup data processor. Parse the signup webhook payload and extract contact information. Respond JSON.",
        "Parse this signup: {webhook_payload}. Extract subscriber_name, subscriber_email, signup_source, and preferred_category. Find each field regardless of format.",
        [
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          { field: "subscriber_email", as: "subscriber_email", type: "string" },
          { field: "signup_source", as: "signup_source", type: "string" },
          {
            field: "preferred_category",
            as: "preferred_category",
            type: "string",
          },
        ],
      ),

      n("2", "runCode", "Generate unique discount code", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate a unique, memorable discount code
const prefix = "WELCOME";
const random = Math.random().toString(36).substring(2, 8).toUpperCase();
const code = prefix + random;
const expiryDate = new Date();
expiryDate.setDate(expiryDate.getDate() + 7);
return {
  discount_code: code,
  discount_amount: "15",
  discount_type: "percentage",
  min_order: "50",
  expiry_date: expiryDate.toISOString().split("T")[0],
  code_generated: "true"
};`,
          outputVariables: [
            { name: "discount_code", type: "string" },
            { name: "discount_amount", type: "string" },
            { name: "discount_type", type: "string" },
            { name: "min_order", type: "string" },
            { name: "expiry_date", type: "string" },
            { name: "code_generated", type: "string" },
          ],
        },
      }),

      // 3. AI welcome email
      aiNode(
        "3",
        "AI: Compose welcome email with discount",
        520,
        "You are a conversion-focused welcome email writer. Write a warm welcome email that prominently features the discount code, creates urgency with the expiry date, and guides the reader to their first purchase. Respond JSON.",
        "Write a welcome email for {subscriber_name}. Discount: {discount_amount}% off with code {discount_code}. Min order: ${min_order}. Expires: {expiry_date}. Source: {signup_source}. Preferred category: {preferred_category}. Return email_subject (with discount mention), email_body (warm welcome with prominent discount and CTA), and preview_text (preheader).",
        [
          { field: "email_subject", as: "welcome_subject", type: "string" },
          { field: "email_body", as: "welcome_body", type: "string" },
          { field: "preview_text", as: "preview_text", type: "string" },
        ],
      ),

      // 4. Welcome email dispatch
      emailNode(
        "4",
        "Email: Welcome with discount code",
        760,
        "{subscriber_email}",
        "{welcome_subject}",
        "Hi {subscriber_name},\\n\\n{welcome_body}\\n\\nYour code: {discount_code}\\n{discount_amount}% off orders over ${min_order}\\nExpires: {expiry_date}\\n\\nHappy shopping!\\n{company_name}",
      ),

      // 5. Redemption reminder (delay + condition)
      n("5", "delayAction", "Wait 5 days before redemption check", 1000, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "5",
          delayUnit: "days",
          description:
            "Waiting 5 days to check if the discount code has been redeemed before sending a reminder or expiring the code",
        },
      }),

      // 6. Redemption tracker + cleanup (Sheets)
      sheetsNode(
        "6",
        "Log discount code for tracking and cleanup",
        1240,
        "DiscountCodes",
        '[["{current_date}","{subscriber_name}","{subscriber_email}","{discount_code}","{discount_amount}%","{min_order}","{expiry_date}","{signup_source}","pending"]]',
      ),
    ],
    edges: [
      e("1", "1b"),
      e("1b", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
    ],
  },

  // ===================================================================
  // 966. DISCOUNT REMINDER WELCOME (7 nodes)
  // ===================================================================

  "Discount Reminder Welcome": {
    summary:
      "Runs a multi-email discount welcome sequence. Provisions a discount code via runCode, sends an initial welcome email with the discount, delays then sends a mid-sequence reminder, checks redemption via condition, sends a final urgency email for non-redeemers, and logs all outcomes to Sheets.",
    nodes: [
      trigger("Webhook: New subscriber trigger", "webhook_app", {
        source: "email_platform",
        event: "subscriber.created",
        howItStarts:
          "A new subscriber joins the email list, triggering a 3-email discount welcome sequence designed to drive first purchase",
        setup:
          "Configure email platform webhook for new subscribers. Set discount code format and expiry rules. Connect Google Sheets for outcome tracking. Define email cadence (Day 0, Day 3, Day 6).",
        inputFields: [],
      }),

      // 2. Discount code provisioning (runCode) + AI extraction
      aiNode(
        "1b",
        "AI: Extract subscriber details",
        180,
        "You are a subscriber data processor. Parse the new subscriber webhook. Respond JSON.",
        "Parse this subscriber event: {webhook_payload}. Extract subscriber_name, subscriber_email, signup_source, and interests. Find each field regardless of format.",
        [
          { field: "subscriber_name", as: "subscriber_name", type: "string" },
          { field: "subscriber_email", as: "subscriber_email", type: "string" },
          { field: "signup_source", as: "signup_source", type: "string" },
          { field: "interests", as: "subscriber_interests", type: "string" },
        ],
      ),

      n("2", "runCode", "Provision discount code with expiry", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Provision a discount code with 7-day expiry
const prefix = "SAVE";
const random = Math.random().toString(36).substring(2, 7).toUpperCase();
const code = prefix + random;
const now = new Date();
const expiry = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
const midReminder = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);
return {
  discount_code: code,
  discount_pct: "20",
  min_order_amount: "40",
  expiry_date: expiry.toISOString().split("T")[0],
  mid_reminder_date: midReminder.toISOString().split("T")[0],
  days_until_expiry: "7",
  code_provisioned: "true"
};`,
          outputVariables: [
            { name: "discount_code", type: "string" },
            { name: "discount_pct", type: "string" },
            { name: "min_order_amount", type: "string" },
            { name: "expiry_date", type: "string" },
            { name: "mid_reminder_date", type: "string" },
            { name: "days_until_expiry", type: "string" },
            { name: "code_provisioned", type: "string" },
          ],
        },
      }),

      // 3. Email #1 welcome + discount
      emailNode(
        "3",
        "Email #1: Welcome + discount code",
        520,
        "{subscriber_email}",
        "Welcome! Here's {discount_pct}% off your first order",
        "Hi {subscriber_name},\\n\\nWelcome to {company_name}! We're excited to have you.\\n\\nAs a thank-you for subscribing, here's an exclusive discount:\\n\\nCode: {discount_code}\\nDiscount: {discount_pct}% off\\nMin. Order: ${min_order_amount}\\nExpires: {expiry_date}\\n\\nBrowse our collection and find something you love!\\n\\nHappy shopping,\\n{company_name}",
      ),

      // 4. Email #2 mid-sequence reminder (delay)
      n("4", "delayAction", "Wait 3 days for mid-sequence reminder", 760, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "3",
          delayUnit: "days",
          description:
            "Waiting 3 days before sending the mid-sequence reminder email about the discount code",
        },
      }),

      // 5. Redemption check condition
      n("5", "condition", "Has discount code been provisioned?", 1000, {
        conditionData: [
          {
            variable: "code_provisioned",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Email #3 final urgency
      emailNode(
        "6",
        "Email #3: Final urgency reminder",
        1240,
        "{subscriber_email}",
        "Last chance: Your {discount_pct}% discount expires soon!",
        "Hi {subscriber_name},\\n\\nJust a friendly reminder — your exclusive {discount_pct}% discount is expiring on {expiry_date}.\\n\\nCode: {discount_code}\\nDiscount: {discount_pct}% off orders over ${min_order_amount}\\n\\nDon't miss out — this is your last chance to save!\\n\\nShop now before it expires.\\n\\n{company_name}",
      ),

      // 7. Outcome logger (Sheets)
      sheetsNode(
        "7",
        "Log discount sequence outcomes",
        1480,
        "DiscountSequence",
        '[["{current_date}","{subscriber_name}","{subscriber_email}","{discount_code}","{discount_pct}%","{expiry_date}","{signup_source}","{subscriber_interests}","sequence_complete"]]',
      ),
    ],
    edges: [
      e("1", "1b"),
      e("1b", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // code active -> send final urgency
      e("5", "7", "output_2"), // skip -> just log
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 967. DISCOURSE REPLY DRAFT (7 nodes)
  // ===================================================================

  "Discourse Reply Draft": {
    summary:
      "Monitors a Discourse forum for new posts, fetches thread context, generates an AI reply, checks quality and policy compliance via condition, queues in Slack for approval, publishes the approved reply, and handles failures with escalation.",
    nodes: [
      trigger("Webhook: New Discourse post monitor", "webhook_app", {
        source: "discourse",
        event: "post.created",
        howItStarts:
          "A new post is created in the Discourse forum that matches monitoring criteria (specific categories, unanswered questions, or topics requiring official response), triggering automated reply drafting",
        setup:
          "Configure Discourse webhook for post creation events. Set category filters and keyword triggers. Connect OpenAI for reply generation. Set up Slack for approval queue. Configure Discourse API for reply publishing.",
        inputFields: [],
      }),

      // 2. Thread context fetch
      aiNode(
        "2",
        "AI: Extract post and thread context",
        280,
        "You are a community forum analyst. Parse the Discourse post webhook and extract all relevant context for generating a helpful reply. Understand the question, the poster's intent, and any existing thread context. Respond JSON.",
        "Parse this Discourse post: {webhook_payload}. Extract post_id, topic_id, topic_title, post_body, author_username, author_trust_level, category_name, tag_list, reply_count, is_first_post (true/false), and question_type (how-to/bug/feature-request/discussion/other). Identify the core question or topic being discussed.",
        [
          { field: "post_id", as: "post_id", type: "string" },
          { field: "topic_id", as: "topic_id", type: "string" },
          { field: "topic_title", as: "topic_title", type: "string" },
          { field: "post_body", as: "post_body", type: "string" },
          { field: "author_username", as: "author_username", type: "string" },
          {
            field: "author_trust_level",
            as: "author_trust_level",
            type: "string",
          },
          { field: "category_name", as: "category_name", type: "string" },
          { field: "reply_count", as: "reply_count", type: "number" },
          { field: "is_first_post", as: "is_first_post", type: "string" },
          { field: "question_type", as: "question_type", type: "string" },
        ],
      ),

      // 3. AI reply composer
      aiNode(
        "3",
        "AI: Compose forum reply",
        520,
        "You are a knowledgeable community manager. Write a helpful, friendly forum reply that directly addresses the poster's question or contribution. Be specific, provide code examples or links when relevant, and maintain a professional yet approachable tone. Do NOT use corporate jargon. Respond JSON.",
        "Write a Discourse reply. Topic: {topic_title}. Post by @{author_username} (trust level {author_trust_level}): {post_body}. Category: {category_name}. Question type: {question_type}. Is first post: {is_first_post}. Existing replies: {reply_count}. Return reply_text (helpful, specific reply with markdown formatting), confidence_score (1-10 how confident in accuracy), sources_referenced (documentation links if applicable), tone_match (formal/casual/technical), and policy_compliant (true/false with reason if false).",
        [
          { field: "reply_text", as: "reply_text", type: "string" },
          { field: "confidence_score", as: "confidence_score", type: "number" },
          {
            field: "sources_referenced",
            as: "sources_referenced",
            type: "string",
          },
          { field: "tone_match", as: "tone_match", type: "string" },
          { field: "policy_compliant", as: "policy_compliant", type: "string" },
        ],
      ),

      // 4. Quality + policy check condition
      n("4", "condition", "Reply passes quality and policy check?", 760, {
        conditionData: [
          {
            variable: "confidence_score",
            value: "6",
            comparison_operator: "GREATER THAN OR EQUAL TO",
            logical_operator: "AND",
          },
          {
            variable: "policy_compliant",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Approval queue (Slack)
      slackNode(
        "5",
        "Slack: Reply approval queue",
        1000,
        "Discourse Reply Draft\\n\\nTopic: {topic_title}\\nCategory: {category_name}\\nAuthor: @{author_username}\\nType: {question_type}\\n\\nOriginal Post:\\n{post_body}\\n\\nDrafted Reply:\\n{reply_text}\\n\\nConfidence: {confidence_score}/10\\nTone: {tone_match}\\nSources: {sources_referenced}\\nPolicy: {policy_compliant}\\n\\nReact with a checkmark to approve publishing.",
      ),

      // 6. Reply publisher
      n("6", "hitApi", "Publish reply to Discourse", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{discourse_domain}/posts.json",
          headers: [
            { key: "Api-Key", value: "{discourse_api_key}" },
            { key: "Api-Username", value: "{discourse_bot_username}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "topic_id", value: "{topic_id}" },
            { key: "raw", value: "{reply_text}" },
            { key: "reply_to_post_number", value: "{post_id}" },
          ],
          variables: [
            { name: "id", customName: "published_post_id", type: "string" },
            { name: "topic_slug", customName: "topic_slug", type: "string" },
          ],
        },
      }),

      // 7. Failure + escalation handler
      slackNode(
        "7",
        "Slack: Reply status and escalation",
        1480,
        "Discourse Reply Published\\n\\nTopic: {topic_title} ({category_name})\\nReply to: @{author_username}\\nPost ID: {published_post_id}\\nTopic: https://{discourse_domain}/t/{topic_slug}/{topic_id}\\n\\nConfidence: {confidence_score}/10\\nQuestion Type: {question_type}\\nSources: {sources_referenced}\\n\\nReply successfully published to Discourse forum.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // passes quality -> send to approval
      e("4", "7", "output_2"), // fails quality -> escalate to human
      e("5", "6"),
      e("6", "7"),
    ],
  },
};
